<template>
  <div>

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h5 class="page-title">{{ activeTabLabel }}</h5>
        <p class="page-sub">Recruitment &rsaquo; {{ activeTabLabel }}</p>
      </div>
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
        <a :href="careersUrl" target="_blank" class="btn-careers" title="Open public careers page">
          <font-awesome-icon icon="external-link-alt" /> Careers Portal
        </a>
        <button class="btn-icon btn-copy-link" @click="copyCareersLink" :title="careersLinkCopied ? 'Copied!' : 'Copy careers link'">
          <font-awesome-icon :icon="careersLinkCopied ? 'check' : 'link'" />
          {{ careersLinkCopied ? 'Copied!' : 'Copy Link' }}
        </button>
      </div>
    </div>

    <!-- ════════════════════  TAB: WORKFORCE PLANS  ════════════════════ -->
    <div v-if="activeTab === 'plans'">
      <div class="section-bar">
        <p class="section-title">Workforce Plans</p>
        <button v-if="canManageWFP" class="btn-primary" @click="openCreatePlan"><font-awesome-icon icon="plus" /> New Plan</button>
      </div>

      <div v-if="plansLoading" class="sk-table"><div v-for="n in 4" :key="n" class="sk-row"></div></div>
      <div v-else class="table-card">
        <table class="data-table">
          <thead><tr><th>Reference</th><th>Name</th><th>Period</th><th>Priority</th><th>Department</th><th>Headcount</th><th>Budget</th><th>Status</th><th></th></tr></thead>
          <tbody>
            <tr v-for="p in plans" :key="p.id">
              <td class="mono">{{ p.reference }}</td>
              <td class="fw-600">{{ p.name }}</td>
              <td class="text-sm">
                <span v-if="p.start_date">{{ fmtDate(p.start_date) }} – {{ fmtDate(p.end_date) }}</span>
                <span v-else>{{ p.period_type }} {{ p.year }}</span>
              </td>
              <td>
                <span v-if="p.priority" class="chip" :class="priorityClass(p.priority)">{{ p.priority }}</span>
                <span v-else>—</span>
              </td>
              <td>{{ p.department?.name || '—' }}</td>
              <td class="text-center">{{ p.total_headcount || (p.roles?.length ? p.roles.reduce((s, r) => s + (r.position_count || 0), 0) : '—') }}</td>
              <td>{{ p.estimated_budget ? fmt(p.estimated_budget) : '—' }}</td>
              <td><span class="chip" :class="planStatusClass(p.status)">{{ planStatusLabel(p.status) }}</span></td>
              <td class="actions-cell">
                <button v-if="canManageWFP && ['draft','rejected'].includes(p.status)" class="btn-icon" @click="openEditPlan(p)" title="Edit"><font-awesome-icon icon="pen" /></button>
                <button v-if="canManageWFP && ['draft','rejected'].includes(p.status)" class="btn-success-sm" @click="submitPlan(p.id)" title="Submit for Approval">Submit</button>
                <button v-if="canManageWFP && p.status === 'pending_approval'" class="btn-icon" @click="cancelPlan(p.id)" title="Cancel"><font-awesome-icon icon="ban" /></button>
                <button class="btn-icon" @click="openPlanEntries(p)" title="Headcount Entries"><font-awesome-icon icon="table" /></button>
                <button v-if="canDeleteWFP && ['draft','rejected'].includes(p.status)" class="btn-icon btn-icon--red" @click="deletePlan(p)" title="Delete"><font-awesome-icon icon="trash" /></button>
              </td>
            </tr>
            <tr v-if="!plans.length"><td colspan="9" class="empty-cell">No workforce plans yet</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ════════════════════  TAB: REQUISITIONS  ════════════════════ -->
    <div v-if="activeTab === 'requisitions'">
      <div class="filter-bar">
        <select class="filter-select" v-model="reqFilters.status"><option value="">All Statuses</option><option value="draft">Draft</option><option value="pending_approval">Pending Approval</option><option value="approved">Approved</option><option value="rejected">Rejected</option></select>
        <input type="text" class="filter-input" v-model="reqFilters.search" placeholder="Search position…" />
        <button class="btn-primary" @click="fetchRequisitions"><font-awesome-icon icon="search" /></button>
        <button v-if="canManageReq" class="btn-primary" style="margin-left:auto" @click="openCreateReq"><font-awesome-icon icon="plus" /> New Requisition</button>
      </div>

      <div v-if="reqLoading" class="sk-table"><div v-for="n in 5" :key="n" class="sk-row"></div></div>
      <div v-else class="table-card">
        <table class="data-table">
          <thead><tr><th>Reference</th><th>Position</th><th>Department</th><th>Type</th><th>Status</th><th>Requested On</th><th></th></tr></thead>
          <tbody>
            <tr v-for="r in requisitions" :key="r.id">
              <td class="mono">{{ r.reference }}</td>
              <td class="fw-600">{{ r.job_position?.name || r.position || '—' }}</td>
              <td>{{ r.department?.name || '—' }}</td>
              <td>{{ r.employment_type || '—' }}</td>
              <td><span class="chip" :class="reqStatusClass(r.status)">{{ reqStatusLabel(r.status) }}</span></td>
              <td>{{ fmtDate(r.created_at) }}</td>
              <td class="actions-cell">
                <button v-if="canManageReq && ['draft','rejected'].includes(r.status)" class="btn-icon" @click="openEditReq(r)" title="Edit"><font-awesome-icon icon="pen" /></button>
                <button v-if="canManageReq && ['draft','rejected'].includes(r.status)" class="btn-success-sm" @click="submitReq(r.id)" title="Submit for Approval">Submit</button>
                <button v-if="canManageReq && r.status === 'pending_approval'" class="btn-icon" @click="cancelReq(r.id)" title="Cancel"><font-awesome-icon icon="ban" /></button>
                <button v-if="canDeleteReq && ['draft','rejected'].includes(r.status)" class="btn-icon btn-icon--red" @click="deleteReq(r.id)" title="Delete"><font-awesome-icon icon="trash" /></button>
                <button v-if="canManagePosting && r.status === 'approved' && !r.posting" class="btn-primary-sm" @click="openCreatePosting(r)" title="Create Posting">+ Posting</button>
              </td>
            </tr>
            <tr v-if="!requisitions.length"><td colspan="7" class="empty-cell">No requisitions found</td></tr>
          </tbody>
        </table>
        <div class="pagination" v-if="reqMeta.last_page > 1">
          <button class="page-btn" :disabled="reqMeta.current_page === 1" @click="goReqPage(reqMeta.current_page - 1)">‹</button>
          <span class="page-info">{{ reqMeta.current_page }} / {{ reqMeta.last_page }}</span>
          <button class="page-btn" :disabled="reqMeta.current_page === reqMeta.last_page" @click="goReqPage(reqMeta.current_page + 1)">›</button>
        </div>
      </div>
    </div>

    <!-- ════════════════════  TAB: JOB POSTINGS  ════════════════════ -->
    <div v-if="activeTab === 'postings'">
      <!-- Sub-nav: status tabs + create button -->
      <div class="int-subnav">
        <div class="int-subnav-tabs">
          <button class="int-subnav-tab" :class="{ 'int-subnav-tab--active': postingFilters.status === '' }" @click="postingFilters.status = ''; fetchPostings()">All</button>
          <button class="int-subnav-tab" :class="{ 'int-subnav-tab--active': postingFilters.status === 'draft' }" @click="postingFilters.status = 'draft'; fetchPostings()">Draft</button>
          <button class="int-subnav-tab" :class="{ 'int-subnav-tab--active': postingFilters.status === 'published' }" @click="postingFilters.status = 'published'; fetchPostings()">Published</button>
          <button class="int-subnav-tab" :class="{ 'int-subnav-tab--active': postingFilters.status === 'closed' }" @click="postingFilters.status = 'closed'; fetchPostings()">Closed</button>
        </div>
        <div style="display:flex;gap:8px;align-items:center">
          <input type="text" class="filter-input" style="width:180px" v-model="postingFilters.search" placeholder="Search title…" @keydown.enter="fetchPostings" />
          <button v-if="canManagePosting" class="btn-primary" @click="openNewPosting"><font-awesome-icon icon="plus" /> New Job Posting</button>
        </div>
      </div>

      <div v-if="postingsLoading" class="sk-table" style="margin-top:12px"><div v-for="n in 4" :key="n" class="sk-row"></div></div>
      <div v-else class="table-card" style="margin-top:12px">
        <table class="data-table">
          <thead><tr><th>Reference</th><th>Title / Job Code</th><th>Channels</th><th>Slots</th><th>Deadline</th><th>Applicants</th><th>Status</th><th></th></tr></thead>
          <tbody>
            <tr v-for="p in postings" :key="p.id">
              <td class="mono">{{ p.reference }}</td>
              <td>
                <p class="fw-600" style="margin:0">{{ p.title }}</p>
                <p v-if="p.job_code" class="text-sm text-muted" style="margin:0">{{ p.job_code }}</p>
              </td>
              <td>
                <span v-if="p.distribution_channels" class="text-sm">{{ p.distribution_channels.replace(/,/g, ', ') }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center">{{ p.slots || 1 }}</td>
              <td>{{ p.application_deadline ? fmtDate(p.application_deadline) : '—' }}</td>
              <td class="text-center">{{ p.applications_count ?? '—' }}</td>
              <td><span class="chip" :class="postingStatusClass(p.status)">{{ p.status }}</span></td>
              <td class="actions-cell">
                <button v-if="canManagePosting" class="btn-icon" @click="openEditPosting(p)" title="Edit"><font-awesome-icon icon="pen" /></button>
                <button v-if="canManagePosting && p.status !== 'published'" class="btn-success-sm" @click="publishPosting(p.id)">Publish</button>
                <button v-if="canManagePosting && p.status === 'published'" class="btn-icon" @click="unpublishPosting(p.id)" title="Unpublish"><font-awesome-icon icon="eye-slash" /></button>
                <button class="btn-icon" @click="viewApplicationsForPosting(p)" title="View Applications"><font-awesome-icon icon="users" /></button>
                <button v-if="canManagePosting && p.status !== 'published'" class="btn-icon btn-icon--red" @click="deletePosting(p.id)" title="Delete"><font-awesome-icon icon="trash" /></button>
              </td>
            </tr>
            <tr v-if="!postings.length"><td colspan="8" class="empty-cell">No job postings yet</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ════════════════════  TAB: APPLICATIONS  ════════════════════ -->
    <div v-if="activeTab === 'applications'">
      <div class="filter-bar">
        <select class="filter-select" v-model="appFilters.status"><option value="">All Stages</option><option value="applied">Applied</option><option value="shortlisted">Shortlisted</option><option value="interviewing">Interviewing</option><option value="selected">Selected</option><option value="hired">Hired</option><option value="rejected">Rejected</option></select>
        <select class="filter-select" v-model="appFilters.job_posting_id">
          <option value="">All Postings</option>
          <option v-for="p in postings" :key="p.id" :value="p.id">{{ p.title }}</option>
        </select>
        <input type="text" class="filter-input" v-model="appFilters.search" placeholder="Search candidate…" />
        <button class="btn-primary" @click="fetchApplications"><font-awesome-icon icon="search" /></button>
      </div>

      <!-- Pipeline board -->
      <div class="pipeline-board">
        <div v-for="col in pipelineColumns" :key="col.status" class="pipeline-col">
          <div class="pipeline-col-head" :class="'head--' + col.color">
            <span>{{ col.label }}</span>
            <span class="count-badge">{{ applicationsByStatus(col.status).length }}</span>
          </div>
          <div class="pipeline-col-body">
            <div v-for="app in applicationsByStatus(col.status)" :key="app.id" class="candidate-card" @click="openAppDetail(app)">
              <p class="cand-name">{{ app.candidate?.first_name }} {{ app.candidate?.last_name }}</p>
              <p class="cand-meta">{{ app.posting?.title || '—' }}</p>
              <p class="cand-date">{{ fmtDate(app.applied_at) }}</p>
              <div class="cand-actions" @click.stop>
                <button v-if="canManageApp && col.status !== 'shortlisted' && !app.offer && !app.completed_interviews_count" class="cand-action-btn cand-action-btn--blue" @click="moveApp(app, 'shortlisted')">Shortlist</button>
                <button v-if="canManageApp && col.status === 'shortlisted'" class="cand-action-btn cand-action-btn--purple" @click="moveApp(app, 'interviewing')">Interview</button>
                <button v-if="canManageApp && col.status === 'interviewing'" class="cand-action-btn cand-action-btn--green" @click="moveApp(app, 'selected')">Select</button>
                <button v-if="canManageApp && !['hired','rejected'].includes(col.status) && !app.offer" class="cand-action-btn cand-action-btn--red" @click="moveApp(app, 'rejected')">Reject</button>
              </div>
            </div>
            <div v-if="!applicationsByStatus(col.status).length" class="pipeline-empty">No candidates</div>
          </div>
        </div>
      </div>

      <!-- Flat list below pipeline -->
      <div v-if="appLoading" class="sk-table" style="margin-top:16px"><div v-for="n in 4" :key="n" class="sk-row"></div></div>
      <div v-else-if="applications.length" class="table-card" style="margin-top:16px">
        <table class="data-table">
          <thead><tr><th>Candidate</th><th>Email</th><th>Posting</th><th>Stage</th><th>Applied</th><th></th></tr></thead>
          <tbody>
            <tr v-for="a in applications" :key="a.id">
              <td class="fw-600">{{ a.candidate?.first_name }} {{ a.candidate?.last_name }}</td>
              <td>{{ a.candidate?.email }}</td>
              <td>{{ a.posting?.title || '—' }}</td>
              <td><span class="chip" :class="appStatusClass(a.status)">{{ appStatusLabel(a.status) }}</span></td>
              <td>{{ fmtDate(a.applied_at) }}</td>
              <td class="actions-cell">
                <button class="btn-icon" @click="openAppDetail(a)" title="View"><font-awesome-icon icon="eye" /></button>
                <button v-if="canManageInterview && a.status === 'shortlisted'" class="btn-primary-sm" @click="scheduleInterview(a)">Schedule Interview</button>
                <button v-if="canManageOffer && a.status === 'selected'" class="btn-success-sm" @click="createOffer(a)">Create Offer</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ════════════════════  TAB: INTERVIEWS  ════════════════════ -->
    <div v-if="activeTab === 'interviews'">

      <!-- Sub-nav bar -->
      <div class="int-subnav">
        <div class="int-subnav-tabs">
          <button class="int-subnav-tab" :class="{ 'int-subnav-tab--active': intView === 'list' }" @click="intView = 'list'">Interviews</button>
          <button v-if="canManageInterview" class="int-subnav-tab" :class="{ 'int-subnav-tab--active': intView === 'types' }" @click="intView = 'types'; fetchInterviewTypes()">Interview Types</button>
          <button v-if="canManageInterview" class="int-subnav-tab" :class="{ 'int-subnav-tab--active': intView === 'templates' }" @click="intView = 'templates'; fetchAssessmentTemplates()">Assessment Setup</button>
        </div>
        <button v-if="canManageInterview && intView === 'list'" class="btn-primary" @click="openScheduleInterview"><font-awesome-icon icon="plus" /> Schedule Interview</button>
        <button v-if="canManageInterview && intView === 'types'" class="btn-primary" @click="openCreateInterviewType"><font-awesome-icon icon="plus" /> New Type</button>
        <button v-if="canManageInterview && intView === 'templates'" class="btn-primary" @click="openCreateTemplate"><font-awesome-icon icon="plus" /> New Template</button>
      </div>

      <!-- ── Interview List ── -->
      <div v-if="intView === 'list'">
        <div class="filter-bar" style="margin-top:14px">
          <select class="filter-select" v-model="intFilters.status"><option value="">All Statuses</option><option value="scheduled">Scheduled</option><option value="rescheduled">Rescheduled</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option></select>
          <input type="date" class="filter-select" v-model="intFilters.from" />
          <input type="date" class="filter-select" v-model="intFilters.to" />
          <button class="btn-primary" @click="fetchInterviews"><font-awesome-icon icon="search" /></button>
        </div>

        <div v-if="intLoading" class="sk-table"><div v-for="n in 5" :key="n" class="sk-row"></div></div>
        <div v-else class="int-board">
          <div v-for="s in interviews" :key="s.id" class="int-card" :class="{ 'int-card--cancelled': s.status === 'cancelled' }">
            <!-- candidate avatar + name -->
            <div class="int-card-head">
              <div class="int-av-circle">{{ initials(s.application?.candidate) }}</div>
              <div>
                <p class="int-card-name">{{ s.application?.candidate?.first_name }} {{ s.application?.candidate?.last_name }}</p>
                <p class="int-card-role">{{ s.application?.posting?.title || '—' }}</p>
              </div>
              <span class="chip" :class="intStatusClass(s.status)" style="margin-left:auto">{{ s.status }}</span>
            </div>
            <!-- type + stage badges -->
            <div class="int-card-badges">
              <span class="int-type-badge" :class="intTypeBadgeClass(s.type)">{{ s.type }}</span>
              <span class="chip chip--blue" style="font-size:10px">{{ stageLabel(s.stage) }}</span>
            </div>
            <!-- date/time -->
            <div class="int-card-dt">
              <font-awesome-icon icon="calendar-alt" class="int-card-icon" />
              <span>{{ fmtDateTime(s.scheduled_at) }}</span>
            </div>
            <!-- meeting link -->
            <div v-if="s.location_or_link" class="int-card-dt">
              <font-awesome-icon icon="link" class="int-card-icon" />
              <a :href="s.location_or_link.startsWith('http') ? s.location_or_link : '#'" target="_blank" class="int-link" rel="noopener">{{ s.location_or_link }}</a>
            </div>
            <!-- interviewers -->
            <div v-if="s.interviewers?.length" class="int-card-ivs">
              <span v-for="iv in s.interviewers" :key="iv.id" class="int-avatar" :title="iv.user?.first_name + ' ' + iv.user?.last_name">{{ initials(iv.user) }}</span>
            </div>
            <!-- actions -->
            <div class="int-card-actions">
              <button class="btn-icon" @click="openIntDetail(s)" title="View / Assess"><font-awesome-icon icon="eye" /></button>
              <button v-if="canManageInterview && ['scheduled','rescheduled'].includes(s.status)" class="btn-icon" @click="openReschedule(s)" title="Reschedule"><font-awesome-icon icon="calendar-alt" /></button>
              <button v-if="canManageInterview && ['scheduled','rescheduled'].includes(s.status)" class="btn-icon btn-icon--red" @click="cancelInterview(s.id)" title="Cancel"><font-awesome-icon icon="ban" /></button>
            </div>
          </div>
          <div v-if="!interviews.length" class="empty-board-msg">No interviews found for the selected filters.</div>
        </div>
      </div>

      <!-- ── Interview Types ── -->
      <div v-if="intView === 'types'" style="margin-top:14px">
        <div v-if="intTypesLoading" class="sk-table"><div v-for="n in 3" :key="n" class="sk-row"></div></div>
        <div v-else class="table-card">
          <table class="data-table">
            <thead><tr><th>Name</th><th>Description</th><th></th></tr></thead>
            <tbody>
              <tr v-for="t in interviewTypes" :key="t.id">
                <td class="fw-600">{{ t.name }}</td>
                <td class="text-muted-sm">{{ t.description || '—' }}</td>
                <td class="actions-cell">
                  <button class="btn-icon" @click="openEditInterviewType(t)" title="Edit"><font-awesome-icon icon="pen" /></button>
                  <button class="btn-icon btn-icon--red" @click="deleteInterviewType(t)" title="Delete"><font-awesome-icon icon="trash" /></button>
                </td>
              </tr>
              <tr v-if="!interviewTypes.length"><td colspan="3" class="empty-cell">No interview types yet. Create one to get started.</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Assessment Templates ── -->
      <div v-if="intView === 'templates'" style="margin-top:14px">
        <div v-if="templatesLoading" class="sk-table"><div v-for="n in 3" :key="n" class="sk-row"></div></div>
        <div v-else class="table-card">
          <table class="data-table">
            <thead><tr><th>Name</th><th>Category</th><th>Criteria</th><th>Description</th><th></th></tr></thead>
            <tbody>
              <tr v-for="t in assessmentTemplates" :key="t.id">
                <td class="fw-600">{{ t.name }}</td>
                <td><span v-if="t.category" class="chip chip--blue" style="font-size:10px">{{ t.category }}</span><span v-else>—</span></td>
                <td class="text-center">{{ t.criteria?.length || 0 }}</td>
                <td class="text-muted-sm">{{ t.description || '—' }}</td>
                <td class="actions-cell">
                  <button class="btn-icon" @click="openTemplateCriteria(t)" title="Manage Criteria"><font-awesome-icon icon="list" /></button>
                  <button class="btn-icon" @click="openEditTemplate(t)" title="Edit"><font-awesome-icon icon="pen" /></button>
                  <button class="btn-icon btn-icon--red" @click="deleteTemplate(t)" title="Delete"><font-awesome-icon icon="trash" /></button>
                </td>
              </tr>
              <tr v-if="!assessmentTemplates.length"><td colspan="5" class="empty-cell">No assessment templates yet.</td></tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>

    <!-- ════════════════════  TAB: OFFER LETTERS  ════════════════════ -->
    <div v-if="activeTab === 'offers'">

      <!-- Sub-nav -->
      <div class="int-subnav">
        <div class="int-subnav-tabs">
          <button class="int-subnav-tab" :class="{ 'int-subnav-tab--active': offerView === 'list' }" @click="offerView = 'list'">Offer Letters</button>
          <button v-if="canManageOffer" class="int-subnav-tab" :class="{ 'int-subnav-tab--active': offerView === 'templates' }" @click="offerView = 'templates'; fetchOfferTemplates()">Templates</button>
        </div>
        <button v-if="canManageOffer && offerView === 'list'" class="btn-primary" @click="openCandidatePicker"><font-awesome-icon icon="plus" /> Create Offer</button>
        <button v-if="canManageOffer && offerView === 'templates'" class="btn-primary" @click="openCreateOfferTemplate"><font-awesome-icon icon="plus" /> New Template</button>
      </div>

      <!-- ── Offer List ── -->
      <div v-if="offerView === 'list'">
        <!-- Status summary -->
        <div class="offer-summary-row">
          <div class="offer-stat-card">
            <span class="offer-stat-num">{{ offers.filter(o=>o.status==='approved').length }}</span>
            <span class="offer-stat-label">Pending</span>
          </div>
          <div class="offer-stat-card offer-stat-card--green">
            <span class="offer-stat-num">{{ offers.filter(o=>o.status==='accepted').length }}</span>
            <span class="offer-stat-label">Accepted</span>
          </div>
          <div class="offer-stat-card offer-stat-card--red">
            <span class="offer-stat-num">{{ offers.filter(o=>o.status==='declined').length }}</span>
            <span class="offer-stat-label">Declined</span>
          </div>
          <div class="offer-stat-card offer-stat-card--gray">
            <span class="offer-stat-num">{{ offers.filter(o=>o.status==='draft').length }}</span>
            <span class="offer-stat-label">Draft</span>
          </div>
        </div>

        <div class="filter-bar" style="margin-top:14px">
          <select class="filter-select" v-model="offerFilters.status"><option value="">All Statuses</option><option value="draft">Draft</option><option value="pending">Pending Approval</option><option value="in_workflow">In Workflow</option><option value="rejected">Approval Rejected</option><option value="approved">Awaiting Response</option><option value="accepted">Accepted</option><option value="declined">Declined</option></select>
          <input type="text" class="filter-input" v-model="offerFilters.search" placeholder="Search candidate or position…" />
          <button class="btn-primary" @click="fetchOffers"><font-awesome-icon icon="search" /></button>
        </div>

        <div v-if="offersLoading" class="sk-table"><div v-for="n in 4" :key="n" class="sk-row"></div></div>
        <div v-else class="table-card">
          <table class="data-table">
            <thead><tr><th>Candidate</th><th>Position</th><th>Type</th><th>Salary</th><th>Start Date</th><th>Status</th><th>Sent</th><th></th></tr></thead>
            <tbody>
              <tr v-for="o in offers" :key="o.id">
                <td class="fw-600">{{ o.candidate?.first_name }} {{ o.candidate?.last_name }}</td>
                <td>{{ o.position }}</td>
                <td><span v-if="o.employment_type" class="chip chip--blue" style="font-size:10px">{{ o.employment_type }}</span><span v-else>—</span></td>
                <td>{{ o.salary ? fmt(o.salary) : '—' }}</td>
                <td>{{ o.start_date ? fmtDate(o.start_date) : '—' }}</td>
                <td><span class="chip" :class="offerStatusClass(o.status)">{{ offerStatusLabel(o.status) }}</span></td>
                <td>{{ o.sent_at ? fmtDate(o.sent_at) : '—' }}</td>
                <td class="actions-cell">
                  <button class="btn-icon" @click="openOfferPreview(o)" title="Preview"><font-awesome-icon icon="eye" /></button>
                  <button v-if="canManageOffer && o.status === 'draft'" class="btn-icon" @click="openEditOffer(o)" title="Edit"><font-awesome-icon icon="pen" /></button>
                  <button v-if="canManageOffer && ['draft','rejected'].includes(o.status)" class="btn-success-sm" @click="sendOffer(o.id)">Send</button>
                  <button v-if="o.status === 'pending' || o.status === 'in_workflow'" class="btn-outline-sm" disabled title="Awaiting workflow approval">Awaiting Approval</button>
                  <button v-if="canManageOffer && o.status === 'approved'" class="btn-primary-sm" @click="openRecordResponse(o)">Record Response</button>
                  <button v-if="canManageOffer && o.status === 'draft'" class="btn-icon btn-icon--red" @click="deleteOffer(o.id)" title="Delete"><font-awesome-icon icon="trash" /></button>
                </td>
              </tr>
              <tr v-if="!offers.length"><td colspan="8" class="empty-cell">No offer letters yet</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Offer Templates ── -->
      <div v-if="offerView === 'templates'" style="margin-top:14px">
        <div v-if="offerTemplatesLoading" class="sk-table"><div v-for="n in 3" :key="n" class="sk-row"></div></div>
        <div v-else class="table-card">
          <table class="data-table">
            <thead><tr><th>Name</th><th>Preview</th><th>Updated</th><th></th></tr></thead>
            <tbody>
              <tr v-for="t in offerTemplates" :key="t.id">
                <td class="fw-600">{{ t.name }}</td>
                <td class="text-muted-sm" style="max-width:320px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis" v-html="t.offer_body?.replace(/<[^>]+>/g,'').substring(0,120) + '…'"></td>
                <td class="text-muted-sm">{{ fmtDate(t.updated_at) }}</td>
                <td class="actions-cell">
                  <button class="btn-icon" @click="openEditOfferTemplate(t)" title="Edit"><font-awesome-icon icon="pen" /></button>
                  <button class="btn-icon btn-icon--red" @click="deleteOfferTemplate(t)" title="Delete"><font-awesome-icon icon="trash" /></button>
                </td>
              </tr>
              <tr v-if="!offerTemplates.length"><td colspan="4" class="empty-cell">No templates yet. Create one to pre-fill offer letters.</td></tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>

    <!-- ══════════════════════════════════════════════════════════════════
         MODALS
    ═══════════════════════════════════════════════════════════════════ -->

    <!-- Plan entries drawer -->
    <div class="modal-overlay" v-if="entriesModal.open" @click.self="entriesModal.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head"><p class="modal-title">{{ entriesModal.plan?.name }} — Headcount Entries</p><button class="modal-close" @click="entriesModal.open = false"><font-awesome-icon icon="times" /></button></div>
        <div class="modal-body">
          <div class="entry-add-row">
            <input type="text" class="form-input" v-model="entryForm.position" placeholder="Position / Role" style="flex:1" />
            <input type="number" class="form-input" v-model="entryForm.opening_headcount" placeholder="Opening" style="width:90px" min="0" />
            <input type="number" class="form-input" v-model="entryForm.new_hires" placeholder="New Hires" style="width:90px" min="0" />
            <input type="number" class="form-input" v-model="entryForm.exits" placeholder="Exits" style="width:90px" min="0" />
            <button class="btn-primary" :disabled="saving" @click="addEntry">Add</button>
          </div>
          <table class="data-table" style="margin-top:14px">
            <thead><tr><th>Position</th><th>Opening</th><th>New Hires</th><th>Exits</th><th>Closing</th><th></th></tr></thead>
            <tbody>
              <tr v-for="e in entriesModal.entries" :key="e.id">
                <td v-if="e._editing"><input type="text" class="form-input form-input--sm" v-model="e._position" /></td>
                <td v-else class="fw-600">{{ e.position }}</td>
                <td v-if="e._editing"><input type="number" class="form-input form-input--sm" v-model="e._opening" min="0" style="width:70px" /></td>
                <td v-else>{{ e.opening_headcount }}</td>
                <td v-if="e._editing"><input type="number" class="form-input form-input--sm" v-model="e._new_hires" min="0" style="width:70px" /></td>
                <td v-else class="text-green">+{{ e.new_hires }}</td>
                <td v-if="e._editing"><input type="number" class="form-input form-input--sm" v-model="e._exits" min="0" style="width:70px" /></td>
                <td v-else class="text-red">-{{ e.exits }}</td>
                <td class="fw-600">{{ e.closing_headcount }}</td>
                <td class="actions-cell">
                  <template v-if="e._editing">
                    <button class="btn-icon" @click="saveEntry(e)"><font-awesome-icon icon="check" /></button>
                    <button class="btn-icon" @click="e._editing = false"><font-awesome-icon icon="times" /></button>
                  </template>
                  <template v-else>
                    <button class="btn-icon" @click="editEntry(e)"><font-awesome-icon icon="pen" /></button>
                    <button class="btn-icon btn-icon--red" @click="deleteEntry(e)"><font-awesome-icon icon="trash" /></button>
                  </template>
                </td>
              </tr>
              <tr v-if="!entriesModal.entries.length"><td colspan="6" class="empty-cell">No entries yet. Add one above.</td></tr>
            </tbody>
          </table>
        </div>
        <div class="modal-footer"><button class="btn-primary" @click="entriesModal.open = false">Done</button></div>
      </div>
    </div>

    <!-- Requisition create/edit modal -->
    <div class="modal-overlay" v-if="reqModal.open" @click.self="reqModal.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head"><p class="modal-title">{{ reqModal.id ? 'Edit Requisition' : 'New Requisition' }}</p><button class="modal-close" @click="reqModal.open = false"><font-awesome-icon icon="times" /></button></div>
        <div class="modal-body">
          <div class="form-row"><label class="form-label">Workforce Plan (optional)</label>
            <select class="form-input" v-model="reqModal.workforce_plan_id">
              <option value="">— No Workforce Plan —</option>
              <option v-for="p in plans.filter(x => x.status === 'active')" :key="p.id" :value="p.id">{{ p.reference }} — {{ p.name }}</option>
            </select>
          </div>
          <div v-if="!reqModal.workforce_plan_id" class="info-box info-box--warn">A justification is required when no workforce plan is selected.</div>
          <div class="form-cols">
            <div class="form-row"><label class="form-label">Position <span class="req">*</span></label><input type="text" class="form-input" v-model="reqModal.position" /></div>
            <div class="form-row"><label class="form-label">Job Code <span class="req">*</span></label><input type="text" class="form-input" v-model="reqModal.job_code" placeholder="e.g. ENG-2026-001" /></div>
            <div class="form-row"><label class="form-label">Department</label>
              <select class="form-input" v-model="reqModal.department_id"><option value="">— select —</option><option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option></select>
            </div>
            <div class="form-row"><label class="form-label">Employment Type</label>
              <select class="form-input" v-model="reqModal.employment_type"><option value="">— select —</option><option v-for="t in ['permanent','contract','intern','adhoc']" :key="t" :value="t">{{ t }}</option></select>
            </div>
            <div class="form-row"><label class="form-label">Location</label><input type="text" class="form-input" v-model="reqModal.location" /></div>
          </div>
          <div class="form-row"><label class="form-label">Job Description</label><textarea class="form-input" rows="3" v-model="reqModal.job_description"></textarea></div>
          <div class="form-row"><label class="form-label">Requirements</label><textarea class="form-input" rows="2" v-model="reqModal.requirements"></textarea></div>
          <div v-if="!reqModal.workforce_plan_id" class="form-row"><label class="form-label">Justification <span class="req">*</span></label><textarea class="form-input" rows="2" v-model="reqModal.justification"></textarea></div>
        </div>
        <div class="modal-footer"><button class="btn-outline" @click="reqModal.open = false">Cancel</button><button class="btn-primary" :disabled="saving" @click="saveReq">{{ saving ? 'Saving…' : (reqModal.id ? 'Update' : 'Create') }}</button></div>
      </div>
    </div>

    <!-- Job posting create/edit modal (multi-section) -->
    <div class="modal-overlay" v-if="postingModal.open" @click.self="postingModal.open = false">
      <div class="modal-box modal-box--xl">
        <div class="modal-head">
          <p class="modal-title">{{ postingModal.id ? 'Edit Job Posting' : 'Create New Job Posting' }}</p>
          <button class="modal-close" @click="postingModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body posting-form-body">

          <!-- ── Section 1: Source Requisition ── -->
          <div class="posting-section">
            <p class="posting-section-title">1. Source Requisition</p>
            <div v-if="postingModal.requisition" class="info-box info-box--blue">
              <font-awesome-icon icon="link" style="margin-right:6px" />
              Linked to: <strong>{{ postingModal.requisition.reference }}</strong> — {{ postingModal.requisition.position || postingModal.requisition.job_position?.name }}
            </div>
            <div v-else>
              <div class="form-row">
                <label class="form-label">Select Approved Requisition <span class="req">*</span></label>
                <select class="form-input" v-model="postingModal.requisition_id" @change="onPickRequisition">
                  <option value="">— select —</option>
                  <option v-for="r in approvedRequisitions" :key="r.id" :value="r.id">{{ r.reference }} — {{ r.job_position?.name || r.position }}</option>
                </select>
              </div>
            </div>
          </div>

          <!-- ── Section 2: Posting Details ── -->
          <div class="posting-section">
            <p class="posting-section-title">2. Posting Details</p>
            <div class="form-cols">
              <div class="form-row"><label class="form-label">Job Title <span class="req">*</span></label><input type="text" class="form-input" v-model="postingModal.title" placeholder="e.g. Senior Software Engineer" /></div>
              <div class="form-row"><label class="form-label">Job Code <span class="req">*</span></label><input type="text" class="form-input" v-model="postingModal.job_code" placeholder="e.g. ENG-2026-001" /></div>
            </div>
            <div class="form-cols">
              <div class="form-row">
                <label class="form-label">Work Mode</label>
                <select class="form-input" v-model="postingModal.work_mode">
                  <option value="">— select —</option>
                  <option value="on_site">On-site</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="remote">Remote</option>
                </select>
              </div>
              <div class="form-row">
                <label class="form-label">Employment Type</label>
                <select class="form-input" v-model="postingModal.employment_type">
                  <option value="">— select —</option>
                  <option value="permanent">Permanent</option>
                  <option value="contract">Contract</option>
                  <option value="intern">Intern</option>
                  <option value="adhoc">Adhoc</option>
                </select>
              </div>
              <div class="form-row"><label class="form-label">Number of Openings</label><input type="number" class="form-input" v-model.number="postingModal.slots" min="1" placeholder="1" /></div>
            </div>
            <div class="form-row">
              <label class="form-label">Summary <span class="text-muted">(min 20 chars)</span></label>
              <textarea class="form-input" rows="3" v-model="postingModal.summary" placeholder="Brief overview of the role…"></textarea>
            </div>
            <div class="form-row">
              <label class="form-label">Responsibilities <span class="text-muted">(min 20 chars)</span></label>
              <textarea class="form-input" rows="4" v-model="postingModal.responsibilities" placeholder="Key responsibilities and duties…"></textarea>
            </div>
            <div class="form-row">
              <label class="form-label">Requirements / Qualifications</label>
              <div class="quill-wrap">
                <QuillEditor v-model:content="postingModal.requirements" content-type="html" :toolbar="quillToolbar" placeholder="Skills, experience, education required…" theme="snow" />
              </div>
            </div>
            <div class="form-row">
              <label class="form-label">Benefits <span class="text-muted">(optional)</span></label>
              <textarea class="form-input" rows="3" v-model="postingModal.benefits" placeholder="What we offer…"></textarea>
            </div>
          </div>

          <!-- ── Section 3: Compensation ── -->
          <div class="posting-section">
            <p class="posting-section-title">3. Compensation &amp; Benefits</p>
            <div class="form-row">
              <label class="toggle-label">
                <input type="checkbox" v-model="postingModal.compensation_display" style="margin-right:8px" />
                Display compensation range on job post
              </label>
            </div>
            <div v-if="postingModal.compensation_display" class="form-cols">
              <div class="form-row">
                <label class="form-label">Currency</label>
                <select class="form-input" v-model="postingModal.compensation_currency">
                  <option value="NGN">NGN</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div class="form-row"><label class="form-label">Min Salary</label><input type="number" class="form-input" v-model.number="postingModal.compensation_min" min="0" placeholder="0" /></div>
              <div class="form-row"><label class="form-label">Max Salary</label><input type="number" class="form-input" v-model.number="postingModal.compensation_max" min="0" placeholder="0" /></div>
            </div>
          </div>

          <!-- ── Section 4: Application Settings ── -->
          <div class="posting-section">
            <p class="posting-section-title">4. Application Settings</p>
            <div class="form-cols">
              <div class="form-row"><label class="form-label">Application Deadline</label><input type="date" class="form-input" v-model="postingModal.application_deadline" /></div>
              <div class="form-row">
                <label class="form-label">Visibility</label>
                <select class="form-input" v-model="postingModal.publish_type">
                  <option value="internal">Internal Only</option>
                  <option value="external">External (Public)</option>
                  <option value="both">Both</option>
                </select>
              </div>
            </div>
            <div class="posting-checkboxes">
              <label class="toggle-label"><input type="checkbox" v-model="postingModal.collect_resume" style="margin-right:8px" /> Collect Resume / CV</label>
              <label class="toggle-label"><input type="checkbox" v-model="postingModal.collect_cover_letter" style="margin-right:8px" /> Collect Cover Letter</label>
            </div>
          </div>

          <!-- ── Section 5: Distribution Channels ── -->
          <div class="posting-section">
            <p class="posting-section-title">5. Distribution Channels</p>
            <p class="text-sm text-muted" style="margin-bottom:10px">Select where this job will be posted (at least one required).</p>
            <div class="posting-channels">
              <label v-for="ch in postingChannels" :key="ch.value" class="channel-chip" :class="{ 'channel-chip--active': postingModal.channelSet.has(ch.value) }" @click="toggleChannel(ch.value)">
                <font-awesome-icon :icon="ch.icon" />
                {{ ch.label }}
              </label>
            </div>
          </div>

        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="postingModal.open = false">Cancel</button>
          <button class="btn-outline" :disabled="saving" @click="savePosting('draft')">{{ saving ? 'Saving…' : 'Save as Draft' }}</button>
          <button class="btn-primary" :disabled="saving" @click="savePosting('published')">{{ saving ? 'Publishing…' : (postingModal.id ? 'Update &amp; Publish' : 'Publish Post') }}</button>
        </div>
      </div>
    </div>

    <!-- Application detail modal -->
    <div class="modal-overlay" v-if="appDetail.open" @click.self="appDetail.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head">
          <div>
            <p class="modal-title">{{ appDetail.data?.candidate?.first_name }} {{ appDetail.data?.candidate?.last_name }}</p>
            <p class="modal-sub">{{ appDetail.data?.posting?.title }}</p>
          </div>
          <button class="modal-close" @click="appDetail.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" v-if="appDetail.data">
          <div class="detail-grid">
            <div class="detail-item"><span class="detail-label">Email</span><span>{{ appDetail.data.candidate?.email }}</span></div>
            <div class="detail-item"><span class="detail-label">Phone</span><span>{{ appDetail.data.candidate?.phone || '—' }}</span></div>
            <div class="detail-item"><span class="detail-label">Stage</span><span class="chip" :class="appStatusClass(appDetail.data.status)">{{ appStatusLabel(appDetail.data.status) }}</span></div>
            <div class="detail-item"><span class="detail-label">Applied</span><span>{{ fmtDate(appDetail.data.applied_at) }}</span></div>
          </div>

          <div class="section-sub-title" style="margin-top:16px">Move Stage</div>
          <div class="stage-actions">
            <button v-for="s in ['applied','shortlisted','interviewing','selected','hired','rejected']" :key="s" class="chip chip-btn" :class="appDetail.data.status === s ? appStatusClass(s) : 'chip--gray'" @click="moveAppFromDetail(s)">{{ appStatusLabel(s) }}</button>
          </div>

          <div class="section-sub-title" style="margin-top:16px">Notes</div>
          <div class="note-add">
            <textarea class="form-input" rows="2" v-model="newNote" placeholder="Add a note…" style="flex:1"></textarea>
            <button class="btn-primary" @click="addNote">Add</button>
          </div>
          <div class="note-list">
            <div v-for="n in appDetail.data.notes" :key="n.id" class="note-item">
              <div class="note-meta"><span class="note-author">{{ n.author?.first_name }} {{ n.author?.last_name }}</span> · <span class="note-date">{{ fmtDate(n.created_at) }}</span></div>
              <p class="note-content">{{ n.content }}</p>
              <button class="note-delete" @click="deleteNote(n.id)"><font-awesome-icon icon="times" /></button>
            </div>
            <p v-if="!appDetail.data.notes?.length" class="text-muted-sm">No notes yet.</p>
          </div>

          <div class="section-sub-title" style="margin-top:16px">Resume</div>
          <div v-if="appDetail.data.resume_url" class="resume-row">
            <font-awesome-icon icon="file-pdf" class="resume-icon" />
            <span class="resume-name">{{ resumeFilename(appDetail.data.resume_path) }}</span>
            <a :href="appDetail.data.resume_url" target="_blank" rel="noopener" download class="btn-download">
              <font-awesome-icon icon="download" /> Download
            </a>
          </div>
          <p v-else class="text-muted-sm">No resume uploaded.</p>

          <div v-if="appDetail.data.cover_letter" class="section-sub-title" style="margin-top:16px">Cover Letter</div>
          <p v-if="appDetail.data.cover_letter" class="cover-letter">{{ appDetail.data.cover_letter }}</p>
        </div>
        <div class="modal-footer">
          <button v-if="appDetail.data?.status === 'shortlisted'" class="btn-primary" @click="scheduleInterview(appDetail.data); appDetail.open = false">Schedule Interview</button>
          <button v-if="appDetail.data?.status === 'selected'" class="btn-success-sm" @click="createOffer(appDetail.data); appDetail.open = false">Create Offer</button>
          <button class="btn-outline" @click="appDetail.open = false">Close</button>
        </div>
      </div>
    </div>

    <!-- Interview schedule / reschedule modal -->
    <div class="modal-overlay" v-if="intModal.open" @click.self="intModal.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head"><p class="modal-title">{{ intModal.id ? 'Reschedule' : 'Schedule' }} Interview</p><button class="modal-close" @click="intModal.open = false"><font-awesome-icon icon="times" /></button></div>
        <div class="modal-body">
          <div v-if="intModal.candidate" class="info-box info-box--blue">Candidate: <strong>{{ intModal.candidate }}</strong></div>
          <div class="form-cols">
            <div class="form-row">
              <label class="form-label">Interview Type <span class="req">*</span></label>
              <select class="form-input" v-model="intModal.type">
                <option value="">— select type —</option>
                <option v-for="t in interviewTypes" :key="t.id" :value="t.name">{{ t.name }}</option>
                <option value="virtual">Virtual</option>
                <option value="physical">Physical</option>
                <option value="phone">Phone</option>
              </select>
            </div>
            <div class="form-row"><label class="form-label">Stage <span class="req">*</span></label>
              <select class="form-input" v-model="intModal.stage"><option value="first">1st Interview</option><option value="technical">Technical</option><option value="hr">HR</option><option value="final">Final</option></select>
            </div>
          </div>
          <div class="form-cols">
            <div class="form-row"><label class="form-label">Date <span class="req">*</span></label><input type="date" class="form-input" v-model="intModal.interview_date" /></div>
            <div class="form-row"><label class="form-label">Time <span class="req">*</span></label><input type="time" class="form-input" v-model="intModal.interview_time" /></div>
          </div>
          <div class="form-row"><label class="form-label">Meeting Link / Location</label><input type="text" class="form-input" v-model="intModal.location_or_link" placeholder="https://meet.google.com/... or room number" /></div>
          <div class="form-row">
            <label class="form-label">Interviewers <span class="req">*</span></label>
            <div class="ms-wrap" @click.stop>
              <div class="ms-control" :class="{ 'ms-control--open': interviewerDropdownOpen }" @click="openInterviewerDropdown">
                <div class="ms-chips">
                  <span v-for="id in intModal.interviewer_ids" :key="id" class="ms-chip">
                    {{ interviewerLabel(id) }}
                    <button class="ms-chip-x" @click.stop="removeInterviewer(id)">×</button>
                  </span>
                  <input ref="ivSearch" v-model="interviewerSearch" class="ms-input" :placeholder="intModal.interviewer_ids.length ? '' : 'Search interviewers…'" @click.stop="openInterviewerDropdown" />
                </div>
                <span class="ms-caret" :class="{ 'ms-caret--up': interviewerDropdownOpen }">▾</span>
              </div>
              <div v-if="interviewerDropdownOpen" class="ms-dropdown" @click.stop>
                <div v-if="!filteredInterviewers.length" class="ms-empty">No users found</div>
                <div v-for="u in filteredInterviewers" :key="u.id" class="ms-option" :class="{ 'ms-option--on': isInterviewerSelected(u.id) }" @click.stop="toggleInterviewer(u.id)">
                  <span class="ms-tick">{{ isInterviewerSelected(u.id) ? '✓' : '' }}</span>
                  <span class="ms-av">{{ initials(u) }}</span>
                  <span>{{ u.first_name }} {{ u.last_name }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="form-row">
            <label class="form-label">Assessment Template <span class="text-muted-sm">(optional)</span></label>
            <select class="form-input" v-model="intModal.assessment_template_id" @change="onTemplateSelect">
              <option value="">— No template —</option>
              <option v-for="t in assessmentTemplates" :key="t.id" :value="t.id">{{ t.name }} <span v-if="t.category">({{ t.category }})</span></option>
            </select>
            <p class="form-hint">Selecting a template auto-fills criteria below.</p>
          </div>
          <div class="form-row">
            <label class="form-label">Competency Criteria</label>
            <div v-for="(c, i) in intModal.competency_criteria" :key="i" class="criteria-row">
              <input type="text" class="form-input" v-model="c.name" placeholder="Criterion name" style="flex:1" />
              <input type="number" class="form-input" v-model="c.weight" placeholder="Weight %" min="0" max="100" style="width:90px" />
              <button class="btn-icon btn-icon--red" @click="intModal.competency_criteria.splice(i,1)"><font-awesome-icon icon="times" /></button>
            </div>
            <button class="btn-outline" style="margin-top:6px;font-size:12px;height:30px" @click="intModal.competency_criteria.push({name:'',weight:0})"><font-awesome-icon icon="plus" /> Add Criterion</button>
          </div>
        </div>
        <div class="modal-footer"><button class="btn-outline" @click="intModal.open = false">Cancel</button><button class="btn-primary" :disabled="saving" @click="saveInterview">{{ saving ? 'Saving…' : (intModal.id ? 'Reschedule' : 'Schedule') }}</button></div>
      </div>
    </div>

    <!-- Interview detail / assessment modal -->
    <div class="modal-overlay" v-if="intDetail.open" @click.self="intDetail.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head">
          <div>
            <p class="modal-title">{{ intDetail.data?.application?.candidate?.first_name }} {{ intDetail.data?.application?.candidate?.last_name }}</p>
            <p class="modal-sub">{{ stageLabel(intDetail.data?.stage) }} · {{ fmtDateTime(intDetail.data?.scheduled_at) }} · {{ intDetail.data?.type }}</p>
          </div>
          <button class="modal-close" @click="intDetail.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" v-if="intDetail.data">

          <!-- Interviewers -->
          <div class="section-sub-title">Interviewers</div>
          <div class="int-avatars" style="margin-bottom:12px">
            <span v-for="iv in intDetail.data.interviewers" :key="iv.id" class="int-avatar-lg">
              {{ initials(iv.user) }} <span style="font-size:10px;margin-left:4px">{{ iv.user?.first_name }}</span>
            </span>
          </div>

          <!-- Existing ratings -->
          <div class="section-sub-title">Submitted Assessments</div>
          <div v-for="r in intDetail.data.ratings" :key="r.id" class="rating-item">
            <div class="rating-head">
              <span class="fw-600">{{ r.interviewer?.first_name }} {{ r.interviewer?.last_name }}</span>
              <span class="chip" :class="recommendationClass(r.decision)">{{ recommendationLabel(r.decision) }}</span>
              <span class="text-muted-sm" style="margin-left:auto">{{ r.submitted_at ? fmtDate(r.submitted_at) : 'Draft' }}</span>
            </div>
            <div v-if="r.scores?.length" class="score-grid">
              <div v-for="s in r.scores" :key="s.criterion" class="score-row">
                <span class="score-criterion">{{ s.criterion }}</span>
                <div class="score-bar-wrap"><div class="score-bar-fill" :style="{width: Math.min(s.score, 100) + '%'}"></div></div>
                <span class="score-val">{{ s.score }}<span class="text-muted-sm">/100</span></span>
              </div>
            </div>
            <p v-if="r.overall_comment" class="note-content" style="margin-top:6px">{{ r.overall_comment }}</p>
          </div>
          <p v-if="!intDetail.data.ratings?.length" class="text-muted-sm">No assessments submitted yet.</p>

          <!-- Submit own assessment if assigned -->
          <template v-if="isAssignedInterviewer(intDetail.data) && !hasAlreadyRated(intDetail.data)">
            <div class="section-sub-title" style="margin-top:20px">Your Assessment</div>
            <div v-if="intDetail.data.competency_criteria?.length">
              <div v-for="(c, i) in intDetail.data.competency_criteria" :key="i" class="assess-criterion-card">
                <div class="assess-criterion-head">
                  <span class="fw-600">{{ c.name }}</span>
                  <span v-if="c.weight" class="chip chip--blue" style="font-size:10px">{{ c.weight }}%</span>
                </div>
                <div class="assess-score-row">
                  <label class="form-label" style="min-width:56px">Score (0–100)</label>
                  <input type="number" class="form-input" style="width:90px" min="0" max="100" v-model="ratingForm.scores[i].score" placeholder="0" />
                </div>
                <div class="form-row" style="margin-top:6px">
                  <textarea class="form-input" rows="2" v-model="ratingForm.scores[i].comment" placeholder="Feedback on this criterion…"></textarea>
                </div>
              </div>
            </div>
            <p v-else class="text-muted-sm" style="margin-bottom:10px">No criteria defined for this interview.</p>

            <!-- Recommendation -->
            <div class="form-row" style="margin-top:8px">
              <label class="form-label">Recommendation <span class="req">*</span></label>
              <div class="recommendation-cards">
                <div class="rec-card rec-card--reject" :class="{ 'rec-card--selected': ratingForm.decision === 'do_not_proceed' }" @click="ratingForm.decision = 'do_not_proceed'">
                  <font-awesome-icon icon="times-circle" class="rec-icon" />
                  <span>Do Not Proceed</span>
                </div>
                <div class="rec-card rec-card--ok" :class="{ 'rec-card--selected': ratingForm.decision === 'recommend' }" @click="ratingForm.decision = 'recommend'">
                  <font-awesome-icon icon="check-circle" class="rec-icon" />
                  <span>Recommend</span>
                </div>
                <div class="rec-card rec-card--strong" :class="{ 'rec-card--selected': ratingForm.decision === 'strong_recommend' }" @click="ratingForm.decision = 'strong_recommend'">
                  <font-awesome-icon icon="star" class="rec-icon" />
                  <span>Strong Recommend</span>
                </div>
              </div>
            </div>

            <div class="form-row"><label class="form-label">Overall Summary <span class="req">*</span></label>
              <textarea class="form-input" rows="3" v-model="ratingForm.overall_comment" placeholder="Write a final summary of the candidate's performance…"></textarea>
            </div>
          </template>
          <div v-else-if="hasAlreadyRated(intDetail.data)" class="info-box info-box--blue" style="margin-top:12px">
            You have already submitted your assessment for this interview.
          </div>
        </div>
        <div class="modal-footer">
          <button v-if="isAssignedInterviewer(intDetail.data) && !hasAlreadyRated(intDetail.data)" class="btn-primary" :disabled="saving" @click="submitRating">{{ saving ? 'Submitting…' : 'Submit Assessment' }}</button>
          <button class="btn-outline" @click="intDetail.open = false">Close</button>
        </div>
      </div>
    </div>

    <!-- Interview type create/edit modal -->
    <div class="modal-overlay" v-if="intTypeModal.open" @click.self="intTypeModal.open = false">
      <div class="modal-box">
        <div class="modal-head"><p class="modal-title">{{ intTypeModal.id ? 'Edit' : 'New' }} Interview Type</p><button class="modal-close" @click="intTypeModal.open = false"><font-awesome-icon icon="times" /></button></div>
        <div class="modal-body">
          <div class="form-row"><label class="form-label">Name <span class="req">*</span></label><input type="text" class="form-input" v-model="intTypeModal.name" placeholder="e.g. Virtual, Technical Panel, Culture Fit" /></div>
          <div class="form-row"><label class="form-label">Description</label><textarea class="form-input" rows="2" v-model="intTypeModal.description" placeholder="Describe this interview type…"></textarea></div>
        </div>
        <div class="modal-footer"><button class="btn-outline" @click="intTypeModal.open = false">Cancel</button><button class="btn-primary" :disabled="saving" @click="saveInterviewType">{{ saving ? 'Saving…' : 'Save' }}</button></div>
      </div>
    </div>

    <!-- Assessment template create/edit modal -->
    <div class="modal-overlay" v-if="templateModal.open" @click.self="templateModal.open = false">
      <div class="modal-box">
        <div class="modal-head"><p class="modal-title">{{ templateModal.id ? 'Edit' : 'New' }} Assessment Template</p><button class="modal-close" @click="templateModal.open = false"><font-awesome-icon icon="times" /></button></div>
        <div class="modal-body">
          <div class="form-row"><label class="form-label">Template Name <span class="req">*</span></label><input type="text" class="form-input" v-model="templateModal.name" /></div>
          <div class="form-row"><label class="form-label">Category</label>
            <select class="form-input" v-model="templateModal.category">
              <option value="">— None —</option>
              <option value="Technical">Technical</option>
              <option value="Behavioural">Behavioural</option>
              <option value="Culture">Culture</option>
              <option value="Leadership">Leadership</option>
            </select>
          </div>
          <div class="form-row"><label class="form-label">Description</label><textarea class="form-input" rows="2" v-model="templateModal.description"></textarea></div>
        </div>
        <div class="modal-footer"><button class="btn-outline" @click="templateModal.open = false">Cancel</button><button class="btn-primary" :disabled="saving" @click="saveTemplate">{{ saving ? 'Saving…' : 'Save' }}</button></div>
      </div>
    </div>

    <!-- Assessment template criteria modal -->
    <div class="modal-overlay" v-if="criteriaModal.open" @click.self="criteriaModal.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head"><p class="modal-title">{{ criteriaModal.template?.name }} — Criteria</p><button class="modal-close" @click="criteriaModal.open = false"><font-awesome-icon icon="times" /></button></div>
        <div class="modal-body">
          <!-- Add criterion form -->
          <div class="criteria-add-grid">
            <input type="text" class="form-input" v-model="criterionForm.name" placeholder="Criterion name" />
            <input type="text" class="form-input" v-model="criterionForm.description" placeholder="Description (optional)" />
            <input type="number" class="form-input" v-model="criterionForm.weight" placeholder="Weight %" min="0" max="100" style="width:100px" />
            <button class="btn-primary" :disabled="saving" @click="addCriterion">Add</button>
          </div>
          <p class="form-hint" style="margin-bottom:10px">Total weight: <strong>{{ totalCriteriaWeight }}%</strong> (aim for 100%)</p>

          <table class="data-table">
            <thead><tr><th>#</th><th>Name</th><th>Description</th><th>Weight %</th><th></th></tr></thead>
            <tbody>
              <tr v-for="(c, i) in criteriaModal.criteria" :key="c.id">
                <td class="text-muted-sm">{{ i + 1 }}</td>
                <td v-if="c._editing"><input type="text" class="form-input form-input--sm" v-model="c._name" /></td>
                <td v-else class="fw-600">{{ c.name }}</td>
                <td v-if="c._editing"><input type="text" class="form-input form-input--sm" v-model="c._description" /></td>
                <td v-else class="text-muted-sm">{{ c.description || '—' }}</td>
                <td v-if="c._editing"><input type="number" class="form-input form-input--sm" v-model="c._weight" min="0" max="100" style="width:80px" /></td>
                <td v-else>{{ c.weight }}%</td>
                <td class="actions-cell">
                  <template v-if="c._editing">
                    <button class="btn-icon" @click="saveCriterion(c)"><font-awesome-icon icon="check" /></button>
                    <button class="btn-icon" @click="c._editing = false"><font-awesome-icon icon="times" /></button>
                  </template>
                  <template v-else>
                    <button class="btn-icon" @click="editCriterion(c)"><font-awesome-icon icon="pen" /></button>
                    <button class="btn-icon btn-icon--red" @click="deleteCriterion(c)"><font-awesome-icon icon="trash" /></button>
                  </template>
                </td>
              </tr>
              <tr v-if="!criteriaModal.criteria.length"><td colspan="5" class="empty-cell">No criteria yet. Add one above.</td></tr>
            </tbody>
          </table>
        </div>
        <div class="modal-footer"><button class="btn-primary" @click="criteriaModal.open = false">Done</button></div>
      </div>
    </div>

    <!-- Candidate picker modal (Step 1: select who to create offer for) -->
    <div class="modal-overlay" v-if="candidatePicker.open" @click.self="candidatePicker.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head">
          <div>
            <p class="modal-title">Select Candidate</p>
            <p class="modal-sub">Only candidates in the "Selected" stage are eligible for an offer.</p>
          </div>
          <button class="modal-close" @click="candidatePicker.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <!-- Filter row -->
          <div class="filter-bar" style="margin-bottom:0">
            <select class="filter-select" v-model="candidatePicker.postingFilter" @change="filterPickerCandidates">
              <option value="">All Postings</option>
              <option v-for="p in postings" :key="p.id" :value="p.id">{{ p.title }}</option>
            </select>
            <input type="text" class="filter-input" v-model="candidatePicker.search" @input="filterPickerCandidates" placeholder="Search candidate name…" style="flex:1" />
          </div>

          <div v-if="candidatePicker.loading" class="sk-table" style="margin-top:12px"><div v-for="n in 4" :key="n" class="sk-row"></div></div>
          <div v-else class="table-card" style="margin-top:12px">
            <table class="data-table">
              <thead><tr><th>Candidate</th><th>Email</th><th>Posting</th><th>Applied</th><th></th></tr></thead>
              <tbody>
                <tr v-for="app in filteredPickerApps" :key="app.id">
                  <td>
                    <div class="picker-cand-row">
                      <div class="int-av-circle" style="width:30px;height:30px;font-size:11px">{{ initials(app.candidate) }}</div>
                      <span class="fw-600">{{ app.candidate?.first_name }} {{ app.candidate?.last_name }}</span>
                    </div>
                  </td>
                  <td class="text-muted-sm">{{ app.candidate?.email }}</td>
                  <td>{{ app.posting?.title || '—' }}</td>
                  <td class="text-muted-sm">{{ fmtDate(app.applied_at) }}</td>
                  <td>
                    <button class="btn-primary-sm" @click="pickCandidate(app)">Select</button>
                  </td>
                </tr>
                <tr v-if="!filteredPickerApps.length">
                  <td colspan="5" class="empty-cell">
                    <span v-if="candidatePicker.loading">Loading…</span>
                    <span v-else>No candidates in the "Selected" stage. Move a candidate to "Selected" from the Applications tab first.</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer"><button class="btn-outline" @click="candidatePicker.open = false">Cancel</button></div>
      </div>
    </div>

    <!-- Offer letter create/edit modal -->
    <div class="modal-overlay" v-if="offerModal.open" @click.self="offerModal.open = false">
      <div class="modal-box modal-box--xl">
        <div class="modal-head">
          <p class="modal-title">{{ offerModal.id ? 'Edit Offer Letter' : 'New Offer Letter' }}</p>
          <button class="modal-close" @click="offerModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div v-if="offerModal.candidate" class="info-box info-box--blue">Candidate: <strong>{{ offerModal.candidate }}</strong></div>

          <!-- Basic details -->
          <div class="form-row"><label class="form-label">Position <span class="req">*</span></label><input type="text" class="form-input" v-model="offerModal.position" /></div>
          <div class="form-cols">
            <div class="form-row">
              <label class="form-label">Employment Type</label>
              <select class="form-input" v-model="offerModal.employment_type">
                <option value="">— select —</option>
                <option value="permanent">Permanent / Full-Time</option>
                <option value="contract">Contract</option>
                <option value="intern">Internship</option>
                <option value="adhoc">Ad-hoc / Part-Time</option>
              </select>
            </div>
            <div class="form-row"><label class="form-label">Annual Salary</label><input type="number" class="form-input" v-model="offerModal.salary" min="0" placeholder="0.00" /></div>
            <div class="form-row"><label class="form-label">Start Date</label><input type="date" class="form-input" v-model="offerModal.start_date" /></div>
            <div class="form-row">
              <label class="form-label">Department</label>
              <select class="form-input" v-model="offerModal.department_id">
                <option value="">— select —</option>
                <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
              </select>
            </div>
            <div class="form-row">
              <label class="form-label">Line Manager</label>
              <select class="form-input" v-model="offerModal.line_manager">
                <option value="">— select —</option>
                <option v-for="u in allUsers" :key="u.id" :value="u.id">{{ u.first_name }} {{ u.last_name }}</option>
              </select>
            </div>
          </div>

          <!-- Signatories -->
          <div class="form-cols">
            <div class="form-row">
              <label class="form-label">Signatory 1</label>
              <select class="form-input" v-model="offerModal.signatory_one">
                <option value="">— select —</option>
                <option v-for="u in allUsers" :key="u.id" :value="u.id">{{ u.first_name }} {{ u.last_name }}</option>
              </select>
            </div>
            <div class="form-row">
              <label class="form-label">Signatory 2</label>
              <select class="form-input" v-model="offerModal.signatory_two">
                <option value="">— select —</option>
                <option v-for="u in allUsers" :key="u.id" :value="u.id">{{ u.first_name }} {{ u.last_name }}</option>
              </select>
            </div>
          </div>

          <!-- Template picker -->
          <div class="form-row">
            <label class="form-label">Apply Template</label>
            <select class="form-input" v-model="offerModal.selected_template_id" @change="onOfferTemplateSelect">
              <option value="">— start from scratch —</option>
              <option v-for="t in offerTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
            <p class="form-hint">Selecting a template fills the offer body below.</p>
          </div>

          <!-- Offer body rich text -->
          <div class="form-row">
            <label class="form-label">Offer Body <span class="req">*</span></label>
            <div class="quill-wrap">
              <QuillEditor v-model:content="offerModal.offer_body" content-type="html" :toolbar="quillToolbar" placeholder="Write the full offer letter content here…" theme="snow" />
            </div>
          </div>

          <!-- Optional plain-text fields -->
          <div class="form-row"><label class="form-label">Benefits / Perks</label><textarea class="form-input" rows="2" v-model="offerModal.benefits" placeholder="Health insurance, pension, remote work…"></textarea></div>
          <div class="form-row"><label class="form-label">Additional Terms</label><textarea class="form-input" rows="2" v-model="offerModal.additional_terms"></textarea></div>

          <!-- Save as template -->
          <div class="save-as-tmpl-row">
            <label class="radio-label">
              <input type="checkbox" v-model="offerModal.save_as_template" />
              Save offer body as a reusable template
            </label>
            <input v-if="offerModal.save_as_template" type="text" class="form-input" v-model="offerModal.template_name" placeholder="Template name…" style="flex:1;margin-left:10px" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="offerModal.open = false">Cancel</button>
          <button class="btn-primary" :disabled="saving" @click="saveOffer">{{ saving ? 'Saving…' : (offerModal.id ? 'Update' : 'Create') }}</button>
        </div>
      </div>
    </div>

    <!-- Offer preview modal -->
    <div class="modal-overlay" v-if="offerPreview.open" @click.self="offerPreview.open = false">
      <div class="modal-box modal-box--xl">
        <div class="modal-head">
          <p class="modal-title">Offer Letter Preview</p>
          <button class="modal-close" @click="offerPreview.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" v-if="offerPreview.data">
          <div class="offer-letter-doc">
            <div class="offer-doc-header">
              <p class="offer-doc-confidential">Private &amp; Confidential</p>
              <p class="offer-doc-date">{{ fmtDate(offerPreview.data.created_at) }}</p>
            </div>
            <p class="offer-doc-candidate">{{ offerPreview.data.candidate?.first_name }} {{ offerPreview.data.candidate?.last_name }}</p>
            <p class="offer-doc-email">{{ offerPreview.data.candidate?.email }}</p>
            <div class="offer-doc-divider"></div>
            <p class="offer-doc-salutation">Dear {{ offerPreview.data.candidate?.first_name }},</p>
            <div class="offer-doc-body" v-html="offerPreview.data.offer_body || offerBodyFallback(offerPreview.data)"></div>
            <div class="offer-doc-divider"></div>
            <div class="offer-doc-signatories">
              <div class="offer-doc-signatory">
                <div class="offer-sig-line"></div>
                <p class="offer-sig-name">{{ signatoryName(offerPreview.data.signatory_one) || 'Signatory 1' }}</p>
                <p class="offer-sig-title">HR Director</p>
              </div>
              <div class="offer-doc-signatory">
                <div class="offer-sig-line"></div>
                <p class="offer-sig-name">{{ signatoryName(offerPreview.data.signatory_two) || 'Signatory 2' }}</p>
                <p class="offer-sig-title">Managing Director</p>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button v-if="['draft','rejected'].includes(offerPreview.data?.status) && canManageOffer" class="btn-success-sm" @click="sendOffer(offerPreview.data.id); offerPreview.open = false">Send Offer</button>
          <span v-if="offerPreview.data?.status === 'pending' || offerPreview.data?.status === 'in_workflow'" class="chip chip--amber" style="font-size:12px">Awaiting Approval</span>
          <button class="btn-outline" @click="offerPreview.open = false">Close</button>
        </div>
      </div>
    </div>

    <!-- Record response modal -->
    <div class="modal-overlay" v-if="responseModal.open" @click.self="responseModal.open = false">
      <div class="modal-box">
        <div class="modal-head"><p class="modal-title">Record Candidate Response</p><button class="modal-close" @click="responseModal.open = false"><font-awesome-icon icon="times" /></button></div>
        <div class="modal-body">
          <div class="form-row"><label class="form-label">Response <span class="req">*</span></label>
            <div class="radio-group">
              <label class="radio-label"><input type="radio" v-model="responseModal.response" value="accepted" /> Accepted</label>
              <label class="radio-label"><input type="radio" v-model="responseModal.response" value="declined" /> Declined</label>
            </div>
          </div>
          <div class="form-row"><label class="form-label">Notes</label><textarea class="form-input" rows="2" v-model="responseModal.response_note"></textarea></div>
        </div>
        <div class="modal-footer"><button class="btn-outline" @click="responseModal.open = false">Cancel</button><button class="btn-primary" :disabled="saving" @click="saveResponse">{{ saving ? 'Saving…' : 'Save' }}</button></div>
      </div>
    </div>

    <!-- Offer template create/edit modal -->
    <div class="modal-overlay" v-if="offerTemplateModal.open" @click.self="offerTemplateModal.open = false">
      <div class="modal-box modal-box--xl">
        <div class="modal-head">
          <p class="modal-title">{{ offerTemplateModal.id ? 'Edit Template' : 'New Offer Template' }}</p>
          <button class="modal-close" @click="offerTemplateModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row"><label class="form-label">Template Name <span class="req">*</span></label><input type="text" class="form-input" v-model="offerTemplateModal.name" /></div>
          <div class="form-row">
            <label class="form-label">Offer Body <span class="req">*</span></label>
            <div class="quill-wrap">
              <QuillEditor v-model:content="offerTemplateModal.offer_body" content-type="html" :toolbar="quillToolbar" placeholder="Write the offer letter template content…" theme="snow" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="offerTemplateModal.open = false">Cancel</button>
          <button class="btn-primary" :disabled="saving" @click="saveOfferTemplate">{{ saving ? 'Saving…' : 'Save Template' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useStore } from 'vuex';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
  name: 'RecruitmentPage',
  components: { QuillEditor },
  data() {
    return {
      store: useStore(),
      activeTab: 'requisitions',
      saving: false,
      quillToolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'clean'],
      ],
      careersLinkCopied: false,
      tabs: [
        { key: 'plans',        label: 'Workforce Plans' },
        { key: 'requisitions', label: 'Requisitions' },
        { key: 'postings',     label: 'Job Postings' },
        { key: 'applications', label: 'Applications' },
        { key: 'interviews',   label: 'Interviews' },
        { key: 'offers',       label: 'Offer Letters' },
      ],
      departments: [],
      allUsers: [],
      interviewerSearch: '',
      interviewerDropdownOpen: false,

      // Plans
      plans: [], plansLoading: false,
      entriesModal: { open: false, plan: null, entries: [] },
      entryForm: { position: '', opening_headcount: 0, new_hires: 0, exits: 0 },

      // Requisitions
      requisitions: [], reqLoading: false,
      reqMeta: { current_page: 1, last_page: 1 },
      reqFilters: { status: '', search: '', page: 1, length: 15 },
      reqModal: { open: false, id: null, position: '', workforce_plan_id: '', department_id: '', job_code: '', employment_type: '', location: '', job_description: '', requirements: '', justification: '' },

      // Postings
      postings: [], postingsLoading: false,
      postingFilters: { status: '', search: '' },
      postingModal: {
        open: false, id: null,
        requisition_id: null, requisition: null,
        title: '', job_code: '', work_mode: '', employment_type: '', slots: 1,
        summary: '', responsibilities: '', benefits: '',
        compensation_display: false, compensation_currency: 'NGN', compensation_min: null, compensation_max: null,
        application_deadline: '', publish_type: 'external',
        collect_resume: true, collect_cover_letter: false,
        distribution_channels: '',
        channelSet: new Set(['career_site']),
        description: '', requirements: '',
      },
      postingChannels: [
        { value: 'career_site', label: 'Company Career Site', icon: 'building' },
        { value: 'linkedin',    label: 'LinkedIn',            icon: 'share-alt' },
        { value: 'indeed',      label: 'Indeed',              icon: 'globe' },
        { value: 'internal',    label: 'Internal Board',      icon: 'users' },
      ],

      // Applications
      applications: [], appLoading: false,
      appFilters: { status: '', job_posting_id: '', search: '' },
      appDetail: { open: false, data: null },
      newNote: '',
      pipelineColumns: [
        { status: 'applied',      label: 'Applied',      color: 'gray' },
        { status: 'shortlisted',  label: 'Shortlisted',  color: 'blue' },
        { status: 'interviewing', label: 'Interviewing', color: 'purple' },
        { status: 'selected',     label: 'Selected',     color: 'green' },
        { status: 'hired',        label: 'Hired',        color: 'teal' },
        { status: 'rejected',     label: 'Rejected',     color: 'red' },
      ],

      // Interviews
      interviews: [], intLoading: false,
      intFilters: { status: '', from: '', to: '' },
      intView: 'list', // list | types | templates
      intModal: { open: false, id: null, application_id: null, candidate: '', type: '', stage: 'first', interview_date: '', interview_time: '', location_or_link: '', interviewer_ids: [], competency_criteria: [], assessment_template_id: '' },
      intDetail: { open: false, data: null },
      ratingForm: { scores: [], overall_comment: '', decision: '' },

      // Interview types
      interviewTypes: [], intTypesLoading: false,
      intTypeModal: { open: false, id: null, name: '', description: '' },

      // Assessment templates
      assessmentTemplates: [], templatesLoading: false,
      templateModal: { open: false, id: null, name: '', description: '', category: '' },
      criteriaModal: { open: false, template: null, criteria: [] },
      criterionForm: { name: '', description: '', weight: 0 },

      // Offers
      offers: [], offersLoading: false,
      offerFilters: { status: '', search: '' },
      offerView: 'list', // list | templates
      offerModal: {
        open: false, id: null, application_id: null, candidate: '',
        position: '', employment_type: '', salary: '', start_date: '',
        department_id: '', line_manager: '', signatory_one: '', signatory_two: '',
        offer_body: '', benefits: '', additional_terms: '',
        selected_template_id: '', save_as_template: false, template_name: '',
      },
      offerPreview: { open: false, data: null },
      responseModal: { open: false, id: null, response: 'accepted', response_note: '' },

      // Offer templates
      offerTemplates: [], offerTemplatesLoading: false,
      offerTemplateModal: { open: false, id: null, name: '', offer_body: '' },

      // Candidate picker (step 1 of offer creation)
      candidatePicker: {
        open: false, loading: false,
        apps: [], // all selected applications
        postingFilter: '', search: '',
      },
    };
  },

  computed: {
    activeTabLabel() {
      const map = { plans: 'Workforce Plans', requisitions: 'Job Requisitions', postings: 'Job Postings', applications: 'Applications', interviews: 'Interviews', offers: 'Offer Letters' };
      return map[this.activeTab] || 'Recruitment';
    },
    permission()        { return this.$store?.state?.getCurrentUserPermission; },
    isSuperAdmin()      { return this.permission?._role_slug === 'business_owner'; },
    canManageAll()      { return this.isSuperAdmin || !!this.permission?.manage_recruitment; },
    canViewRecruitment(){ return this.canManageAll || !!this.permission?.view_recruitment; },
    canManageWFP()      { return this.canManageAll || !!this.permission?.manage_workforceplan; },
    canDeleteWFP()      { return this.canManageAll || !!this.permission?.manage_workforceplan || !!this.permission?.delete_workforceplan; },
    canManageReq()      { return this.canManageAll || !!this.permission?.manage_requisition; },
    canDeleteReq()      { return this.canManageAll || !!this.permission?.manage_requisition || !!this.permission?.delete_requisition; },
    canManagePosting()  { return this.canManageAll || !!this.permission?.manage_job_posting; },
    canManageApp()      { return this.canManageAll || !!this.permission?.manage_job_application; },
    canManageInterview(){ return this.canManageAll || !!this.permission?.manage_job_interview; },
    canManageOffer()    { return this.canManageAll || !!this.permission?.manage_job_offer; },

    loggedUserId() { return this.$store?.state?.getLoggedInUser?.id || null; },
    filteredPickerApps() {
      let apps = this.candidatePicker.apps;
      if (this.candidatePicker.postingFilter) {
        apps = apps.filter(a => a.posting_id === this.candidatePicker.postingFilter || a.posting?.id === this.candidatePicker.postingFilter);
      }
      const q = this.candidatePicker.search.trim().toLowerCase();
      if (q) {
        apps = apps.filter(a => {
          const name = `${a.candidate?.first_name} ${a.candidate?.last_name}`.toLowerCase();
          return name.includes(q) || a.candidate?.email?.toLowerCase().includes(q);
        });
      }
      return apps;
    },
    totalCriteriaWeight() {
      return (this.criteriaModal.criteria || []).reduce((s, c) => s + (parseFloat(c.weight) || 0), 0).toFixed(0);
    },
    filteredInterviewers() {
      const q = this.interviewerSearch.trim().toLowerCase();
      if (!q) return this.allUsers;
      return this.allUsers.filter(u =>
        (u.first_name + ' ' + u.last_name).toLowerCase().includes(q)
      );
    },
    careersUrl() {
      const code = this.$route?.params?.companyCode || '';
      return `${window.location.origin}/careers/${code}`;
    },
    approvedRequisitions() {
      return this.requisitions.filter(r => r.status === 'approved' && !r.posting);
    },
  },

  watch: {
    'intModal.open'(val) {
      if (!val) {
        this.interviewerDropdownOpen = false;
        this.interviewerSearch = '';
        document.removeEventListener('click', this._closeIvDropdown);
      }
    },
    activeTab(tab) {
      if (tab === 'plans' && !this.plans.length) this.fetchPlans();
      if (tab === 'requisitions') this.fetchRequisitions();
      if (tab === 'postings') this.fetchPostings();
      if (tab === 'applications') this.fetchApplications();
      if (tab === 'interviews') { this.fetchInterviews(); this.fetchInterviewTypes(); this.fetchAssessmentTemplates(); }
      if (tab === 'offers') { this.fetchOffers(); this.fetchOfferTemplates(); }
    },
    '$route.query.tab'(tab) {
      const valid = ['plans', 'requisitions', 'postings', 'applications', 'interviews', 'offers'];
      if (tab && valid.includes(tab)) this.activeTab = tab;
    },
  },

  mounted() {
    const tab = this.$route.query.tab;
    const valid = ['plans', 'requisitions', 'postings', 'applications', 'interviews', 'offers'];
    if (tab && valid.includes(tab)) this.activeTab = tab;
    this.fetchRequisitions();
    this.fetchPlans();
    this.fetchDepartments();
    this.fetchUsers();
    this.fetchInterviewTypes();
    this.fetchAssessmentTemplates();
    this.fetchOfferTemplates();
  },

  methods: {
    // ── Careers portal link ───────────────────────────────────────────
    async copyCareersLink() {
      try { 
        await navigator.clipboard.writeText(this.careersUrl); 
      } catch {/* silent */}
      this.careersLinkCopied = true;
      setTimeout(() => { this.careersLinkCopied = false; }, 2200);
    },

    // ── Support data ──────────────────────────────────────────────────
    async fetchDepartments() {
      try { 
        const { data } = await axios.get('/payroll/department/active'); this.departments = data.data || []; 
      } catch {/* silent */}
    },
    async fetchUsers() {
      try { 
        const { data } = await axios.get('/manage-users'); this.allUsers = data.data?.data || data.data || []; 
      } catch {/* silent */}
    },

    // ── Workforce Plans ───────────────────────────────────────────────
    async fetchPlans() {
      this.plansLoading = true;
      try { const { data } = await axios.get('/hr/recruitment/workforce-plan'); this.plans = data.data || []; } catch { this.$toast?.error('Failed to load plans'); } finally { this.plansLoading = false; }
    },
    openCreatePlan() {
      this.$router.push(this.$url('/hr/recruitment/workforce-plan/create'));
    },
    openEditPlan(p) {
      this.$router.push(this.$url(`/hr/recruitment/workforce-plan/${p.id}/edit`));
    },
    async submitPlan(id) {
      if (!confirm('Submit this plan for approval?')) return;
      try { await axios.put(`/hr/recruitment/workforce-plan/${id}/submit`); this.$toast?.success('Submitted for approval'); this.fetchPlans(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Submit failed'); }
    },
    async cancelPlan(id) {
      if (!confirm('Cancel this submission?')) return;
      try { await axios.put(`/hr/recruitment/workforce-plan/${id}/cancel`); this.$toast?.success('Submission cancelled'); this.fetchPlans(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Cancel failed'); }
    },
    async deletePlan(p) {
      if (!confirm(`Delete plan "${p.name}"?`)) return;
      try { await axios.delete(`/hr/recruitment/workforce-plan/${p.id}`); this.$toast?.success('Deleted'); this.fetchPlans(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Delete failed'); }
    },
    async openPlanEntries(p) {
      this.entriesModal = { open: true, plan: p, entries: [] };
      try { 
        const { data } = await axios.get(`/hr/recruitment/workforce-plan/${p.id}/entries`); this.entriesModal.entries = (data.data || []).map(e => ({ ...e, _editing: false, _position: e.position, _opening: e.opening_headcount, _new_hires: e.new_hires, _exits: e.exits })); 
      } catch {/* silent */}
    },
    async addEntry() {
      if (!this.entryForm.position) { this.$toast?.error('Position is required'); return; }
      this.saving = true;
      try {
        const { data } = await axios.post(`/hr/recruitment/workforce-plan/${this.entriesModal.plan.id}/entries`, this.entryForm);
        const e = data.data; this.entriesModal.entries.push({ ...e, _editing: false, _position: e.position, _opening: e.opening_headcount, _new_hires: e.new_hires, _exits: e.exits });
        this.entryForm = { position: '', opening_headcount: 0, new_hires: 0, exits: 0 }; this.$toast?.success('Entry added');
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); } finally { this.saving = false; }
    },
    editEntry(e) { e._editing = true; },
    async saveEntry(e) {
      try {
        const { data } = await axios.put(`/hr/recruitment/workforce-plan/entries/${e.id}`, { position: e._position, opening_headcount: e._opening, new_hires: e._new_hires, exits: e._exits });
        Object.assign(e, data.data, { _editing: false }); this.$toast?.success('Updated');
      } catch (err) { this.$toast?.error(err.response?.data?.message || 'Failed'); }
    },
    async deleteEntry(e) {
      if (!confirm('Delete this entry?')) return;
      try { 
        await axios.delete(`/hr/recruitment/workforce-plan/entries/${e.id}`); this.entriesModal.entries = this.entriesModal.entries.filter(x => x.id !== e.id); 
      } catch {/* silent */}
    },

    // ── Requisitions ──────────────────────────────────────────────────
    async fetchRequisitions() {
      this.reqLoading = true;
      try { const { data } = await axios.get('/hr/recruitment/requisition', { params: this.reqFilters }); this.requisitions = data.data?.data || []; this.reqMeta = { current_page: data.data?.current_page || 1, last_page: data.data?.last_page || 1 }; } catch { this.$toast?.error('Failed to load requisitions'); } finally { this.reqLoading = false; }
    },
    goReqPage(p) { this.reqFilters.page = p; this.fetchRequisitions(); },
    openCreateReq() { this.$router.push(this.$url('/hr/recruitment/requisition/create')); },
    openEditReq(r)  { this.$router.push(this.$url(`/hr/recruitment/requisition/${r.id}/edit`)); },
    async saveReq() {
      if (!this.reqModal.position) { this.$toast?.error('Position is required'); return; }
      if (!this.reqModal.job_code) { this.$toast?.error('Job Code is required'); return; }
      if (!this.reqModal.workforce_plan_id && !this.reqModal.justification) { this.$toast?.error('Justification is required when no workforce plan is selected'); return; }
      this.saving = true;
      try {
        if (this.reqModal.id) { await axios.put(`/hr/recruitment/requisition/${this.reqModal.id}`, this.reqModal); }
        else { await axios.post('/hr/recruitment/requisition', this.reqModal); }
        this.reqModal.open = false; this.$toast?.success('Saved'); this.fetchRequisitions();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Save failed'); } finally { this.saving = false; }
    },
    async submitReq(id) {
      if (!confirm('Submit this requisition for approval?')) return;
      try { await axios.put(`/hr/recruitment/requisition/${id}/submit`); this.$toast?.success('Submitted'); this.fetchRequisitions(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Submit failed'); }
    },
    async cancelReq(id) {
      if (!confirm('Cancel this requisition?')) return;
      try { await axios.put(`/hr/recruitment/requisition/${id}/cancel`); this.$toast?.success('Cancelled'); this.fetchRequisitions(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); }
    },
    async deleteReq(id) {
      if (!confirm('Delete this requisition?')) return;
      try { await axios.delete(`/hr/recruitment/requisition/${id}`); this.$toast?.success('Deleted'); this.fetchRequisitions(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Delete failed'); }
    },

    // ── Postings ──────────────────────────────────────────────────────
    async fetchPostings() {
      this.postingsLoading = true;
      try { const { data } = await axios.get('/hr/recruitment/posting', { params: this.postingFilters }); this.postings = data.data?.data || []; } catch { this.$toast?.error('Failed to load postings'); } finally { this.postingsLoading = false; }
    },
    openNewPosting() {
      if (!this.requisitions.length) this.fetchRequisitions();
      this.postingModal = {
        open: true, id: null, requisition_id: null, requisition: null,
        title: '', job_code: '', work_mode: '', employment_type: '', slots: 1,
        summary: '', responsibilities: '', benefits: '',
        compensation_display: false, compensation_currency: 'NGN', compensation_min: null, compensation_max: null,
        application_deadline: '', publish_type: 'external',
        collect_resume: true, collect_cover_letter: false,
        distribution_channels: '', channelSet: new Set(['career_site']),
        description: '', requirements: '',
      };
    },
    openCreatePosting(req) {
      this.postingModal = {
        open: true, id: null, requisition_id: req.id, requisition: req,
        title:            req.job_position?.name || req.position || '',
        job_code:         req.job_code || '',
        work_mode:        req.work_arrangement || '',
        employment_type:  req.employment_type || '',
        slots:            req.position_count || 1,
        department_id:    req.department_id || '',
        application_deadline: req.proposed_start_date?.substring(0, 10) || '',
        summary: '', responsibilities: '',
        benefits: '', compensation_display: false, compensation_currency: 'NGN', compensation_min: null, compensation_max: null,
        publish_type: 'external',
        collect_resume: true, collect_cover_letter: false,
        distribution_channels: '', channelSet: new Set(['career_site']),
        description: req.job_description || '', requirements: req.requirements || '',
      };
    },
    openEditPosting(p) {
      const channels = p.distribution_channels ? p.distribution_channels.split(',').map(c => c.trim()).filter(Boolean) : ['career_site'];
      this.postingModal = {
        open: true, ...p,
        requisition: p.requisition,
        employment_type: p.employment_type || '',
        channelSet: new Set(channels),
      };
    },
    onPickRequisition() {
      const req = this.requisitions.find(r => r.id === this.postingModal.requisition_id);
      if (!req) return;
      this.postingModal.title           = req.job_position?.name || req.position || this.postingModal.title;
      this.postingModal.job_code        = req.job_code || this.postingModal.job_code;
      this.postingModal.employment_type = req.employment_type || this.postingModal.employment_type;
      this.postingModal.slots           = req.position_count || this.postingModal.slots;
      this.postingModal.work_mode       = req.work_arrangement || this.postingModal.work_mode;
      this.postingModal.department_id   = req.department_id || this.postingModal.department_id;
      this.postingModal.application_deadline = req.proposed_start_date?.substring(0, 10) || this.postingModal.application_deadline;
      this.postingModal.description     = req.job_description || this.postingModal.description;
      this.postingModal.requirements    = req.requirements || this.postingModal.requirements;
    },
    toggleChannel(val) {
      if (this.postingModal.channelSet.has(val)) {
        this.postingModal.channelSet.delete(val);
      } else {
        this.postingModal.channelSet.add(val);
      }
      this.postingModal.channelSet = new Set(this.postingModal.channelSet);
    },
    async savePosting(status = 'draft') {
      if (!this.postingModal.title) { this.$toast?.error('Job title is required'); return; }
      if (!this.postingModal.job_code) { this.$toast?.error('Job Code is required'); return; }
      if (!this.postingModal.id && !this.postingModal.requisition_id) { this.$toast?.error('Please select a requisition'); return; }
      if (this.postingModal.channelSet.size === 0) { this.$toast?.error('Select at least one distribution channel'); return; }
      this.saving = true;
      try {
        const payload = {
          ...this.postingModal,
          distribution_channels: Array.from(this.postingModal.channelSet).join(','),
          status,
        };
        delete payload.channelSet;
        if (this.postingModal.id) { await axios.put(`/hr/recruitment/posting/${this.postingModal.id}`, payload); }
        else { await axios.post('/hr/recruitment/posting', payload); }
        this.postingModal.open = false;
        this.$toast?.success(status === 'published' ? 'Posting published!' : 'Draft saved');
        this.fetchPostings(); this.fetchRequisitions();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Save failed'); } finally { this.saving = false; }
    },
    async publishPosting(id) { try { await axios.put(`/hr/recruitment/posting/${id}/publish`); this.$toast?.success('Published'); this.fetchPostings(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); } },
    async unpublishPosting(id) { try { await axios.put(`/hr/recruitment/posting/${id}/unpublish`); this.$toast?.success('Unpublished'); this.fetchPostings(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); } },
    async deletePosting(id) { if (!confirm('Delete posting?')) return; try { await axios.delete(`/hr/recruitment/posting/${id}`); this.$toast?.success('Deleted'); this.fetchPostings(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Delete failed'); } },
    viewApplicationsForPosting(p) { this.appFilters.job_posting_id = p.id; this.activeTab = 'applications'; this.fetchApplications(); },

    // ── Applications ──────────────────────────────────────────────────
    async fetchApplications() {
      this.appLoading = true;
      try { const { data } = await axios.get('/hr/recruitment/application', { params: this.appFilters }); this.applications = data.data?.data || []; } catch { this.$toast?.error('Failed to load applications'); } finally { this.appLoading = false; }
    },
    applicationsByStatus(status) { return this.applications.filter(a => a.status === status); },
    async openAppDetail(app) {
      try { const { data } = await axios.get(`/hr/recruitment/application/${app.id}`); this.appDetail = { open: true, data: data.data }; } catch { this.$toast?.error('Failed to load details'); }
    },
    async moveApp(app, status) {
      try { await axios.put(`/hr/recruitment/application/${app.id}/status`, { status }); app.status = status; this.$toast?.success('Stage updated'); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); }
    },
    async moveAppFromDetail(status) {
      if (!this.appDetail.data) return;
      try { await axios.put(`/hr/recruitment/application/${this.appDetail.data.id}/status`, { status }); this.appDetail.data.status = status; this.$toast?.success('Stage updated'); this.fetchApplications(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); }
    },
    async addNote() {
      if (!this.newNote || !this.appDetail.data) return;
      try { const { data } = await axios.post(`/hr/recruitment/application/${this.appDetail.data.id}/notes`, { content: this.newNote }); this.appDetail.data.notes?.unshift(data.data); this.newNote = ''; this.$toast?.success('Note added'); } catch { this.$toast?.error('Failed to add note'); }
    },
    async deleteNote(noteId) {
      if (!confirm('Delete this note?')) return;
      try { 
        await axios.delete(`/hr/recruitment/application/notes/${noteId}`); this.appDetail.data.notes = this.appDetail.data.notes.filter(n => n.id !== noteId); 
      } catch {/* silent */}
    },

    // ── Interviews ────────────────────────────────────────────────────
    async fetchInterviews() {
      this.intLoading = true;
      try { const { data } = await axios.get('/hr/recruitment/interview', { params: this.intFilters }); this.interviews = data.data || []; } catch { this.$toast?.error('Failed to load interviews'); } finally { this.intLoading = false; }
    },
    openScheduleInterview() {
      this.intModal = { open: true, id: null, application_id: null, candidate: '', type: '', stage: 'first', interview_date: '', interview_time: '', location_or_link: '', interviewer_ids: [], competency_criteria: [], assessment_template_id: '' };
    },
    scheduleInterview(app) {
      this.intModal = { open: true, id: null, application_id: app.id, candidate: `${app.candidate?.first_name} ${app.candidate?.last_name}`, type: '', stage: 'first', interview_date: '', interview_time: '', location_or_link: '', interviewer_ids: [], competency_criteria: [], assessment_template_id: '' };
      this.activeTab = 'interviews';
    },
    openReschedule(s) {
      const dt = s.scheduled_at ? new Date(s.scheduled_at) : null;
      this.intModal = {
        open: true, id: s.id, application_id: s.application_id,
        candidate: `${s.application?.candidate?.first_name} ${s.application?.candidate?.last_name}`,
        type: s.type, stage: s.stage,
        interview_date: dt ? dt.toISOString().substring(0,10) : '',
        interview_time: dt ? dt.toTimeString().substring(0,5) : '',
        location_or_link: s.location_or_link || '',
        interviewer_ids: (s.interviewers || []).map(i => i.user_id),
        competency_criteria: s.competency_criteria || [],
        assessment_template_id: '',
      };
    },
    onTemplateSelect() {
      const tmpl = this.assessmentTemplates.find(t => t.id === this.intModal.assessment_template_id);
      if (tmpl?.criteria?.length) {
        this.intModal.competency_criteria = tmpl.criteria.map(c => ({ name: c.name, weight: c.weight }));
      }
    },
    async saveInterview() {
      if ((!this.intModal.interview_date || !this.intModal.interview_time) || !this.intModal.interviewer_ids.length) {
        this.$toast?.error('Date, time and at least one interviewer are required'); return;
      }
      this.saving = true;
      const payload = {
        ...this.intModal,
        scheduled_at: `${this.intModal.interview_date} ${this.intModal.interview_time}`,
      };
      try {
        if (this.intModal.id) { await axios.put(`/hr/recruitment/interview/${this.intModal.id}`, payload); }
        else { await axios.post('/hr/recruitment/interview', payload); }
        this.intModal.open = false; this.$toast?.success(this.intModal.id ? 'Rescheduled' : 'Scheduled'); this.fetchInterviews();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); } finally { this.saving = false; }
    },
    async cancelInterview(id) {
      if (!confirm('Cancel this interview?')) return;
      try { await axios.put(`/hr/recruitment/interview/${id}/cancel`); this.$toast?.success('Cancelled'); this.fetchInterviews(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); }
    },
    async openIntDetail(s) {
      try {
        const { data } = await axios.get(`/hr/recruitment/interview/${s.id}`);
        this.intDetail = { open: true, data: data.data };
        this.ratingForm = {
          scores: (data.data.competency_criteria || []).map(c => ({ criterion: c.name, score: null, comment: '' })),
          overall_comment: '', decision: '',
        };
      } catch { this.$toast?.error('Failed to load'); }
    },
    isAssignedInterviewer(schedule) {
      if (!schedule) return false;
      return (schedule.interviewers || []).some(i => i.user_id === this.loggedUserId);
    },
    hasAlreadyRated(schedule) {
      if (!schedule) return false;
      return (schedule.ratings || []).some(r => r.interviewer_id === this.loggedUserId && r.submitted_at);
    },
    async submitRating() {
      if (!this.ratingForm.decision) { this.$toast?.error('Please select a recommendation'); return; }
      if (!this.ratingForm.overall_comment) { this.$toast?.error('Please enter an overall summary'); return; }
      this.saving = true;
      try {
        await axios.post(`/hr/recruitment/interview/${this.intDetail.data.id}/rating`, this.ratingForm);
        this.$toast?.success('Assessment submitted'); this.intDetail.open = false; this.fetchInterviews();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); } finally { this.saving = false; }
    },

    // ── Candidate Picker (offer creation step 1) ─────────────────────
    async openCandidatePicker() {
      this.candidatePicker = { open: true, loading: true, apps: [], postingFilter: '', search: '' };
      try {
        // Load "selected" applications — also ensure postings are loaded for the filter dropdown
        const { data } = await axios.get('/hr/recruitment/application', { params: { status: 'selected', length: 100 } });
        this.candidatePicker.apps = data.data?.data || [];
      } catch { this.$toast?.error('Failed to load candidates'); }
      finally { this.candidatePicker.loading = false; }
      if (!this.postings.length) this.fetchPostings();
    },
    filterPickerCandidates() { /* reactive via computed */ },
    pickCandidate(app) {
      this.candidatePicker.open = false;
      this.offerModal = {
        open: true, id: null, application_id: app.id,
        candidate: `${app.candidate?.first_name} ${app.candidate?.last_name}`,
        position: app.posting?.requisition?.position || app.posting?.title || '',
        employment_type: app.posting?.requisition?.employment_type || '',
        salary: '', start_date: '', department_id: '', line_manager: '',
        signatory_one: '', signatory_two: '', offer_body: '',
        benefits: '', additional_terms: '',
        selected_template_id: '', save_as_template: false, template_name: '',
      };
    },

    // ── Offer Letters ─────────────────────────────────────────────────
    async fetchOffers() {
      this.offersLoading = true;
      try { const { data } = await axios.get('/hr/recruitment/offer', { params: this.offerFilters }); this.offers = data.data?.data || []; } catch { this.$toast?.error('Failed to load offers'); } finally { this.offersLoading = false; }
    },
    createOffer(app) {
      this.offerModal = {
        open: true, id: null, application_id: app.id,
        candidate: `${app.candidate?.first_name} ${app.candidate?.last_name}`,
        position: app.posting?.requisition?.position || app.posting?.title || '',
        employment_type: app.posting?.requisition?.employment_type || '',
        salary: '', start_date: '', department_id: '', line_manager: '',
        signatory_one: '', signatory_two: '', offer_body: '',
        benefits: '', additional_terms: '',
        selected_template_id: '', save_as_template: false, template_name: '',
      };
      this.activeTab = 'offers';
    },
    openEditOffer(o) {
      this.offerModal = {
        open: true, id: o.id, application_id: o.application_id,
        candidate: `${o.candidate?.first_name} ${o.candidate?.last_name}`,
        position: o.position || '', employment_type: o.employment_type || '',
        salary: o.salary || '', start_date: o.start_date || '',
        department_id: o.department_id || '', line_manager: o.line_manager || '',
        signatory_one: o.signatory_one || '', signatory_two: o.signatory_two || '',
        offer_body: o.offer_body || '', benefits: o.benefits || '',
        additional_terms: o.additional_terms || '',
        selected_template_id: '', save_as_template: false, template_name: '',
      };
    },
    onOfferTemplateSelect() {
      const tmpl = this.offerTemplates.find(t => t.id === this.offerModal.selected_template_id);
      if (tmpl) this.offerModal.offer_body = tmpl.offer_body;
    },
    async saveOffer() {
      if (!this.offerModal.position) { this.$toast?.error('Position is required'); return; }
      this.saving = true;
      try {
        if (this.offerModal.id) {
          await axios.put(`/hr/recruitment/offer/${this.offerModal.id}`, this.offerModal);
        } else {
          await axios.post('/hr/recruitment/offer', this.offerModal);
        }
        // Save as template if requested
        if (this.offerModal.save_as_template && this.offerModal.template_name && this.offerModal.offer_body) {
          await axios.post('/hr/recruitment/offer-template', { name: this.offerModal.template_name, offer_body: this.offerModal.offer_body });
          this.fetchOfferTemplates();
        }
        this.offerModal.open = false; this.$toast?.success('Saved'); this.fetchOffers();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Save failed'); } finally { this.saving = false; }
    },
    async sendOffer(id) {
      if (!confirm('Send this offer letter to the candidate?')) return;
      try {
        const { data } = await axios.put(`/hr/recruitment/offer/${id}/send`);
        this.$toast?.success(data.message || 'Offer sent');
        this.fetchOffers();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); }
    },
    async deleteOffer(id) {
      if (!confirm('Delete this offer?')) return;
      try { await axios.delete(`/hr/recruitment/offer/${id}`); this.$toast?.success('Deleted'); this.fetchOffers(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Delete failed'); }
    },
    openRecordResponse(o) { this.responseModal = { open: true, id: o.id, response: 'accepted', response_note: '' }; },
    async saveResponse() {
      if (!this.responseModal.response) { this.$toast?.error('Please select a response'); return; }
      this.saving = true;
      try { await axios.put(`/hr/recruitment/offer/${this.responseModal.id}/response`, { response: this.responseModal.response, response_note: this.responseModal.response_note }); this.responseModal.open = false; this.$toast?.success('Response recorded'); this.fetchOffers(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); } finally { this.saving = false; }
    },
    async openOfferPreview(o) {
      try {
        const { data } = await axios.get(`/hr/recruitment/offer/${o.id}`);
        this.offerPreview = { open: true, data: data.data };
      } catch { this.$toast?.error('Failed to load offer details'); }
    },
    offerBodyFallback(o) {
      const lines = [];
      if (o.position) lines.push(`<p>We are pleased to offer you the position of <strong>${o.position}</strong>.</p>`);
      if (o.employment_type) lines.push(`<p><strong>Employment Type:</strong> ${o.employment_type}</p>`);
      if (o.salary) lines.push(`<p><strong>Annual Salary:</strong> ${this.fmt(o.salary)}</p>`);
      if (o.start_date) lines.push(`<p><strong>Start Date:</strong> ${this.fmtDate(o.start_date)}</p>`);
      if (o.benefits) lines.push(`<p><strong>Benefits:</strong> ${o.benefits}</p>`);
      if (o.additional_terms) lines.push(`<p><strong>Additional Terms:</strong> ${o.additional_terms}</p>`);
      return lines.join('') || '<p>No offer body provided.</p>';
    },
    signatoryName(userId) {
      if (!userId) return '';
      const u = this.allUsers.find(u => u.id === userId);
      return u ? `${u.first_name} ${u.last_name}` : '';
    },

    // ── Offer Templates ───────────────────────────────────────────────
    async fetchOfferTemplates() {
      this.offerTemplatesLoading = true;
      try { const { data } = await axios.get('/hr/recruitment/offer-template'); this.offerTemplates = data.data || []; } catch {/* silent */} finally { this.offerTemplatesLoading = false; }
    },
    openCreateOfferTemplate() { this.offerTemplateModal = { open: true, id: null, name: '', offer_body: '' }; },
    openEditOfferTemplate(t) { this.offerTemplateModal = { open: true, id: t.id, name: t.name, offer_body: t.offer_body || '' }; },
    async saveOfferTemplate() {
      if (!this.offerTemplateModal.name) { this.$toast?.error('Template name is required'); return; }
      if (!this.offerTemplateModal.offer_body) { this.$toast?.error('Offer body is required'); return; }
      this.saving = true;
      try {
        if (this.offerTemplateModal.id) {
          await axios.put(`/hr/recruitment/offer-template/${this.offerTemplateModal.id}`, this.offerTemplateModal);
        } else {
          await axios.post('/hr/recruitment/offer-template', this.offerTemplateModal);
        }
        this.offerTemplateModal.open = false; this.$toast?.success('Template saved'); this.fetchOfferTemplates();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Save failed'); } finally { this.saving = false; }
    },
    async deleteOfferTemplate(t) {
      if (!confirm(`Delete template "${t.name}"?`)) return;
      try { await axios.delete(`/hr/recruitment/offer-template/${t.id}`); this.$toast?.success('Deleted'); this.fetchOfferTemplates(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Delete failed'); }
    },

    // ── Interview Types ───────────────────────────────────────────────
    async fetchInterviewTypes() {
      this.intTypesLoading = true;
      try { const { data } = await axios.get('/hr/recruitment/interview-type'); this.interviewTypes = data.data || []; } catch {/* silent */} finally { this.intTypesLoading = false; }
    },
    openCreateInterviewType() { this.intTypeModal = { open: true, id: null, name: '', description: '' }; },
    openEditInterviewType(t) { this.intTypeModal = { open: true, id: t.id, name: t.name, description: t.description || '' }; },
    async saveInterviewType() {
      if (!this.intTypeModal.name) { this.$toast?.error('Name is required'); return; }
      this.saving = true;
      try {
        if (this.intTypeModal.id) { await axios.put(`/hr/recruitment/interview-type/${this.intTypeModal.id}`, this.intTypeModal); }
        else { await axios.post('/hr/recruitment/interview-type', this.intTypeModal); }
        this.intTypeModal.open = false; this.$toast?.success('Saved'); this.fetchInterviewTypes();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Save failed'); } finally { this.saving = false; }
    },
    async deleteInterviewType(t) {
      if (!confirm(`Delete "${t.name}"?`)) return;
      try { await axios.delete(`/hr/recruitment/interview-type/${t.id}`); this.$toast?.success('Deleted'); this.fetchInterviewTypes(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Delete failed'); }
    },

    // ── Assessment Templates ──────────────────────────────────────────
    async fetchAssessmentTemplates() {
      this.templatesLoading = true;
      try { const { data } = await axios.get('/hr/recruitment/assessment-template'); this.assessmentTemplates = data.data || []; } catch {/* silent */} finally { this.templatesLoading = false; }
    },
    openCreateTemplate() { this.templateModal = { open: true, id: null, name: '', description: '', category: '' }; },
    openEditTemplate(t) { this.templateModal = { open: true, id: t.id, name: t.name, description: t.description || '', category: t.category || '' }; },
    async saveTemplate() {
      if (!this.templateModal.name) { this.$toast?.error('Template name is required'); return; }
      this.saving = true;
      try {
        if (this.templateModal.id) { await axios.put(`/hr/recruitment/assessment-template/${this.templateModal.id}`, this.templateModal); }
        else { await axios.post('/hr/recruitment/assessment-template', this.templateModal); }
        this.templateModal.open = false; this.$toast?.success('Saved'); this.fetchAssessmentTemplates();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Save failed'); } finally { this.saving = false; }
    },
    async deleteTemplate(t) {
      if (!confirm(`Delete template "${t.name}"? This will also remove its criteria.`)) return;
      try { await axios.delete(`/hr/recruitment/assessment-template/${t.id}`); this.$toast?.success('Deleted'); this.fetchAssessmentTemplates(); } catch (e) { this.$toast?.error(e.response?.data?.message || 'Delete failed'); }
    },
    async openTemplateCriteria(t) {
      try {
        const { data } = await axios.get(`/hr/recruitment/assessment-template/${t.id}`);
        const tmpl = data.data;
        this.criteriaModal = {
          open: true, template: tmpl,
          criteria: (tmpl.criteria || []).map(c => ({ ...c, _editing: false, _name: c.name, _description: c.description, _weight: c.weight })),
        };
        this.criterionForm = { name: '', description: '', weight: 0 };
      } catch { this.$toast?.error('Failed to load template'); }
    },
    async addCriterion() {
      if (!this.criterionForm.name) { this.$toast?.error('Criterion name is required'); return; }
      this.saving = true;
      try {
        const { data } = await axios.post(`/hr/recruitment/assessment-template/${this.criteriaModal.template.id}/criteria`, this.criterionForm);
        const c = data.data;
        this.criteriaModal.criteria.push({ ...c, _editing: false, _name: c.name, _description: c.description, _weight: c.weight });
        this.criterionForm = { name: '', description: '', weight: 0 };
        this.$toast?.success('Criterion added');
        this.fetchAssessmentTemplates();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); } finally { this.saving = false; }
    },
    editCriterion(c) { c._editing = true; },
    async saveCriterion(c) {
      try {
        const { data } = await axios.put(`/hr/recruitment/assessment-template/${this.criteriaModal.template.id}/criteria/${c.id}`, { name: c._name, description: c._description, weight: c._weight });
        Object.assign(c, data.data, { _editing: false }); this.$toast?.success('Updated');
        this.fetchAssessmentTemplates();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); }
    },
    async deleteCriterion(c) {
      if (!confirm('Delete this criterion?')) return;
      try {
        await axios.delete(`/hr/recruitment/assessment-template/${this.criteriaModal.template.id}/criteria/${c.id}`);
        this.criteriaModal.criteria = this.criteriaModal.criteria.filter(x => x.id !== c.id);
        this.$toast?.success('Deleted');
        this.fetchAssessmentTemplates();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); }
    },

    // ── Helpers ───────────────────────────────────────────────────────
    resumeFilename(path) { return path ? path.split('/').pop() : 'resume'; },
    fmt(v) { return v != null ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'; },
    fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
    fmtDateTime(d) { return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'; },
    initials(u) { return u ? (u.first_name?.[0] || '') + (u.last_name?.[0] || '') : '?'; },

    // ── Interviewer multi-select ──────────────────────────────────────
    openInterviewerDropdown() {
      this.interviewerDropdownOpen = true;
      this.$nextTick(() => this.$refs.ivSearch?.focus());
      document.addEventListener('click', this._closeIvDropdown);
    },
    _closeIvDropdown() {
      this.interviewerDropdownOpen = false;
      this.interviewerSearch = '';
      document.removeEventListener('click', this._closeIvDropdown);
    },
    toggleInterviewer(id) {
      const idx = this.intModal.interviewer_ids.indexOf(id);
      if (idx === -1) this.intModal.interviewer_ids.push(id);
      else this.intModal.interviewer_ids.splice(idx, 1);
    },
    removeInterviewer(id) {
      this.intModal.interviewer_ids = this.intModal.interviewer_ids.filter(x => x !== id);
    },
    isInterviewerSelected(id) { return this.intModal.interviewer_ids.includes(id); },
    interviewerLabel(id) {
      const u = this.allUsers.find(u => u.id === id);
      return u ? `${u.first_name} ${u.last_name}` : id;
    },
    stageLabel(s) { return { first: '1st Interview', technical: 'Technical', hr: 'HR', final: 'Final' }[s] || s || '—'; },
    planStatusClass(s) { return { draft: 'chip--gray', active: 'chip--green', closed: 'chip--red', pending_approval: 'chip--amber', approved: 'chip--green', rejected: 'chip--red' }[s] || 'chip--gray'; },
    planStatusLabel(s) { return { draft: 'Draft', active: 'Active', closed: 'Closed', pending_approval: 'Pending Approval', approved: 'Approved', rejected: 'Rejected' }[s] || s; },
    priorityClass(s) { return { high: 'chip--red', medium: 'chip--amber', low: 'chip--blue', High: 'chip--red', Medium: 'chip--amber', Low: 'chip--blue' }[s] || 'chip--gray'; },
    reqStatusClass(s) { return { draft: 'chip--gray', pending_approval: 'chip--amber', approved: 'chip--green', rejected: 'chip--red' }[s] || 'chip--gray'; },
    reqStatusLabel(s) { return { draft: 'Draft', pending_approval: 'Pending Approval', approved: 'Approved', rejected: 'Rejected' }[s] || s; },
    postingStatusClass(s) { return { draft: 'chip--gray', published: 'chip--green', unpublished: 'chip--amber', closed: 'chip--red' }[s] || 'chip--gray'; },
    appStatusClass(s) { return { applied: 'chip--gray', shortlisted: 'chip--blue', interviewing: 'chip--purple', selected: 'chip--amber', hired: 'chip--green', rejected: 'chip--red' }[s] || 'chip--gray'; },
    appStatusLabel(s) { return { applied: 'Applied', shortlisted: 'Shortlisted', interviewing: 'Interviewing', selected: 'Selected', hired: 'Hired', rejected: 'Rejected' }[s] || s; },
    intStatusClass(s) { return { scheduled: 'chip--blue', rescheduled: 'chip--amber', completed: 'chip--green', cancelled: 'chip--red' }[s] || 'chip--gray'; },
    intTypeBadgeClass(t) {
      const map = { virtual: 'type-badge--virtual', physical: 'type-badge--physical', phone: 'type-badge--phone' };
      return map[t?.toLowerCase()] || 'type-badge--other';
    },
    recommendationClass(d) {
      if (d === 'do_not_proceed' || d === 'reject') return 'chip--red';
      if (d === 'strong_recommend') return 'chip--amber';
      if (d === 'recommend' || d === 'proceed') return 'chip--green';
      return 'chip--gray';
    },
    recommendationLabel(d) {
      return { do_not_proceed: 'Do Not Proceed', recommend: 'Recommended', strong_recommend: 'Strong Recommend', proceed: 'Proceed', hold: 'Hold', reject: 'Reject' }[d] || d || 'No decision';
    },
    offerStatusClass(s) { return { draft: 'chip--gray', pending: 'chip--orange', in_workflow: 'chip--orange', rejected: 'chip--red', approved: 'chip--amber', accepted: 'chip--green', declined: 'chip--red' }[s] || 'chip--gray'; },
    offerStatusLabel(s) { return { draft: 'Draft', pending: 'Pending Approval', in_workflow: 'In Workflow', rejected: 'Approval Rejected', approved: 'Awaiting Response', accepted: 'Accepted', declined: 'Declined' }[s] || s; },
  },
};
</script>

<style scoped>
/* Rich editor */
.quill-wrap { border:1px solid #dde5f0; border-radius:8px; overflow:hidden; }
.quill-wrap:focus-within { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.quill-wrap :deep(.ql-toolbar) { border:none; border-bottom:1px solid #e4eaf2; background:#f8fafc; padding:6px 8px; }
.quill-wrap :deep(.ql-container) { border:none; font-size:13px; font-family:inherit; min-height:120px; }
.quill-wrap :deep(.ql-editor) { min-height:120px; padding:10px 12px; color:#1a2b45; line-height:1.6; }
.quill-wrap :deep(.ql-editor.ql-blank::before) { color:#aab8cc; font-style:normal; }
.quill-wrap :deep(.ql-toolbar button:hover .ql-stroke),
.quill-wrap :deep(.ql-toolbar button.ql-active .ql-stroke) { stroke:#004AAD; }
.quill-wrap :deep(.ql-toolbar button:hover .ql-fill),
.quill-wrap :deep(.ql-toolbar button.ql-active .ql-fill) { fill:#004AAD; }
.quill-wrap :deep(.ql-toolbar .ql-picker-label:hover),
.quill-wrap :deep(.ql-toolbar .ql-picker-label.ql-active) { color:#004AAD; }

/* Layout */
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:12px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

/* Careers link buttons */
.btn-careers { display:inline-flex; align-items:center; gap:6px; background:#f0f7ff; color:#004AAD; border:1px solid #bfdbfe; border-radius:8px; padding:0 14px; height:34px; font-size:12px; font-weight:700; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-careers:hover { background:#dbeafe; }
.btn-copy-link { display:inline-flex; align-items:center; gap:6px; background:#f8fafc; color:#64748b; border:1px solid #dde5f0; border-radius:8px; padding:0 12px; height:34px; font-size:12px; font-weight:600; cursor:pointer; transition:all .15s; white-space:nowrap; }
.btn-copy-link:hover { background:#f0f4f8; color:#1a2b45; }

/* Tabs */
.tab-pills-row   { display:flex; gap:4px; flex-wrap:wrap; margin-bottom:20px; }
.tab-pill        { padding:6px 14px; border-radius:20px; border:1px solid #dde5f0; background:#fff; font-size:12px; font-weight:600; color:#7a90b0; cursor:pointer; transition:all .15s; }
.tab-pill:hover  { background:#f0f7ff; color:#004AAD; border-color:#004AAD; }
.tab-pill--active{ background:#004AAD; color:#fff; border-color:#004AAD; }

/* Section bar */
.section-bar  { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.section-title{ font-size:14px; font-weight:700; color:#1a2b45; margin:0; }

/* Buttons */
.btn-primary   { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 16px; height:36px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline   { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 14px; height:36px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-primary-sm{ display:inline-flex; align-items:center; gap:4px; background:#dbeafe; color:#1d4ed8; border:1px solid #93c5fd; border-radius:6px; padding:3px 10px; font-size:11px; font-weight:600; cursor:pointer; }
.btn-success-sm{ display:inline-flex; align-items:center; gap:4px; background:#dcfce7; color:#15803d; border:1px solid #86efac; border-radius:6px; padding:3px 10px; font-size:11px; font-weight:600; cursor:pointer; }
.btn-icon      { background:none; border:none; cursor:pointer; color:#7a90b0; padding:4px 7px; border-radius:6px; font-size:13px; }
.btn-icon:hover { background:#f0f4f8; color:#004AAD; }
.btn-icon--red:hover { color:#dc2626; }

/* Filter bar */
.filter-bar    { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:14px; align-items:center; }
.filter-select,.filter-input { height:36px; padding:0 10px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; }

/* Table */
.table-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-table  { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:11px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody td { padding:11px 14px; color:#1a2b45; vertical-align:middle; border-bottom:1px solid #f0f4f8; }
.data-table tbody tr:last-child td { border-bottom:none; }
.tr-hover { cursor:pointer; transition:background .1s; }
.tr-hover:hover { background:#f8fafc; }
.actions-cell { white-space:nowrap; display:flex; gap:4px; align-items:center; }
.empty-cell { text-align:center; color:#aab8cc; padding:40px !important; }
.fw-600 { font-weight:600; }
.mono { font-family:monospace; font-size:12px; color:#004AAD; }
.text-center { text-align:center; }
.text-green { color:#16a34a; font-weight:600; }
.text-red { color:#dc2626; font-weight:600; }
.text-muted-sm { font-size:12px; color:#7a90b0; }

/* Chips */
.chip { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }
.chip--red    { background:#fee2e2; color:#dc2626; }
.chip--blue   { background:#dbeafe; color:#1d4ed8; }
.chip--purple { background:#ede9fe; color:#7c3aed; }
.chip--gray   { background:#f1f5f9; color:#64748b; }
.chip--teal   { background:#ccfbf1; color:#0f766e; }
.chip-btn { cursor:pointer; transition:opacity .15s; }
.chip-btn:hover { opacity:.8; }

/* Pagination */
.pagination { display:flex; align-items:center; justify-content:center; gap:12px; padding:14px; border-top:1px solid #f0f4f8; }
.page-btn   { background:#fff; border:1px solid #dde5f0; border-radius:6px; padding:4px 12px; font-size:15px; cursor:pointer; }
.page-btn:disabled { opacity:.4; cursor:not-allowed; }
.page-info  { font-size:13px; color:#7a90b0; }

/* Skeleton */
.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row { height:48px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Pipeline board */
.pipeline-board { display:grid; grid-template-columns:repeat(6,minmax(180px,1fr)); gap:12px; overflow-x:auto; padding-bottom:8px; }
.pipeline-col   { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; min-height:200px; }
.pipeline-col-head { padding:10px 14px; font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; display:flex; justify-content:space-between; align-items:center; }
.head--gray   { background:#f1f5f9; color:#64748b; }
.head--blue   { background:#dbeafe; color:#1d4ed8; }
.head--purple { background:#ede9fe; color:#7c3aed; }
.head--green  { background:#dcfce7; color:#16a34a; }
.head--teal   { background:#ccfbf1; color:#0f766e; }
.head--red    { background:#fee2e2; color:#dc2626; }
.count-badge  { background:rgba(0,0,0,.08); border-radius:20px; padding:2px 7px; font-size:11px; }
.pipeline-col-body { padding:8px; display:flex; flex-direction:column; gap:6px; }
.pipeline-empty { text-align:center; padding:20px 8px; font-size:12px; color:#aab8cc; }
.candidate-card { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:10px; cursor:pointer; transition:box-shadow .15s; }
.candidate-card:hover { box-shadow:0 2px 8px rgba(0,0,0,.08); }
.cand-name  { font-size:13px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.cand-meta  { font-size:11px; color:#7a90b0; margin:0 0 4px; }
.cand-date  { font-size:10px; color:#aab8cc; margin:0 0 8px; }
.cand-actions { display:flex; flex-direction:column; gap:3px; }
.cand-action-btn { width:100%; padding:3px 0; border-radius:5px; border:none; font-size:10px; font-weight:600; cursor:pointer; }
.cand-action-btn--blue   { background:#dbeafe; color:#1d4ed8; }
.cand-action-btn--purple { background:#ede9fe; color:#7c3aed; }
.cand-action-btn--green  { background:#dcfce7; color:#16a34a; }
.cand-action-btn--red    { background:#fee2e2; color:#dc2626; }

/* Modals */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box     { background:#fff; border-radius:14px; width:100%; max-width:540px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--wide { max-width:720px; }
.modal-head    { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title   { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-sub     { font-size:12px; color:#7a90b0; margin:3px 0 0; }
.modal-close   { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; }
.modal-body    { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer  { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }
.form-cols     { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.form-row      { display:flex; flex-direction:column; gap:5px; }
.form-label    { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input    { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; }
.form-input:focus { border-color:#004AAD; }
.form-input--sm { padding:5px 8px; font-size:12px; }
.form-hint     { font-size:11px; color:#7a90b0; margin-top:2px; }
.req { color:#dc2626; }

/* Info boxes */
.info-box { padding:10px 14px; border-radius:8px; font-size:13px; }
.info-box--blue { background:#dbeafe; border:1px solid #93c5fd; color:#1e40af; }
.info-box--warn { background:#fef9c3; border:1px solid #fde047; color:#854d0e; }

/* Entries */
.entry-add-row { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }

/* Stage actions */
.stage-actions { display:flex; flex-wrap:wrap; gap:6px; }

/* Notes */
.note-add    { display:flex; gap:8px; }
.note-list   { display:flex; flex-direction:column; gap:8px; margin-top:8px; }
.note-item   { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:10px 14px; position:relative; }
.note-meta   { font-size:11px; color:#7a90b0; margin-bottom:4px; }
.note-author { font-weight:700; color:#1a2b45; }
.note-content{ font-size:13px; color:#1a2b45; margin:0; }
.note-delete { position:absolute; top:8px; right:8px; background:none; border:none; cursor:pointer; color:#aab8cc; font-size:12px; }
.note-delete:hover { color:#dc2626; }

/* Detail grid */
.detail-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.detail-item { display:flex; flex-direction:column; gap:3px; }
.detail-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; }
.section-sub-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#4a5e78; }
.cover-letter { font-size:13px; color:#1a2b45; background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:12px; line-height:1.6; }

/* Interviewer avatars */
.int-avatars    { display:flex; flex-wrap:wrap; gap:4px; }
.int-avatar     { display:inline-flex; align-items:center; justify-content:center; width:28px; height:28px; background:#dbeafe; color:#1d4ed8; border-radius:50%; font-size:10px; font-weight:700; }
.int-avatar-lg  { display:inline-flex; align-items:center; gap:4px; padding:3px 8px; background:#f0f4f8; border-radius:20px; font-size:12px; font-weight:600; color:#1a2b45; }

/* Rating */
.rating-item { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:12px 14px; margin-bottom:8px; }
.rating-head { display:flex; align-items:center; gap:8px; margin-bottom:8px; }
.score-grid  { display:flex; flex-direction:column; gap:6px; }
.score-row   { display:flex; align-items:center; gap:10px; }
.score-criterion { font-size:12px; color:#4a5e78; min-width:130px; }
.score-bar-wrap  { flex:1; height:6px; background:#e4eaf2; border-radius:10px; overflow:hidden; }
.score-bar-fill  { height:100%; background:#004AAD; border-radius:10px; }
.score-val       { font-size:12px; font-weight:700; color:#1a2b45; min-width:24px; text-align:right; }
.score-slider    { flex:1; accent-color:#004AAD; }
.criteria-row    { display:flex; gap:8px; align-items:center; margin-bottom:6px; }
.criteria-score-row { display:flex; align-items:center; gap:10px; padding:4px 0; }

/* Radio */
.radio-group { display:flex; gap:20px; padding:8px 0; }
.radio-label { display:flex; align-items:center; gap:6px; font-size:13px; color:#1a2b45; cursor:pointer; }

/* Resume row */
.resume-row  { display:flex; align-items:center; gap:10px; padding:10px 14px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; margin-top:6px; }
.resume-icon { font-size:18px; color:#dc2626; flex-shrink:0; }
.resume-name { flex:1; font-size:13px; color:#1a2b45; word-break:break-all; }
.btn-download { display:inline-flex; align-items:center; gap:6px; padding:0 14px; height:32px; background:#004AAD; color:#fff; font-size:12px; font-weight:600; border-radius:6px; border:none; cursor:pointer; text-decoration:none; white-space:nowrap; flex-shrink:0; }
.btn-download:hover { background:#003b8e; }

/* ── Offer Letters ───────────────────────────────────────────────── */
.offer-summary-row { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:4px; }
.offer-stat-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:16px; text-align:center; }
.offer-stat-card--green { border-color:#86efac; background:#f0fdf4; }
.offer-stat-card--red   { border-color:#fca5a5; background:#fff1f2; }
.offer-stat-card--gray  { border-color:#e4eaf2; background:#f8fafc; }
.offer-stat-num { display:block; font-size:28px; font-weight:800; color:#1a2b45; }
.offer-stat-card--green .offer-stat-num { color:#16a34a; }
.offer-stat-card--red .offer-stat-num   { color:#dc2626; }
.offer-stat-card--gray .offer-stat-num  { color:#64748b; }
.offer-stat-label { display:block; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-top:4px; }
.modal-box--xl { max-width:860px; }
.save-as-tmpl-row { display:flex; align-items:center; flex-wrap:wrap; gap:6px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:10px 14px; }
.picker-cand-row { display:flex; align-items:center; gap:8px; }

/* ── Offer letter document preview ───────────────────────────────── */
.offer-letter-doc { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:36px 40px; font-size:14px; color:#1a2b45; line-height:1.8; }
.offer-doc-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:24px; }
.offer-doc-confidential { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.08em; color:#dc2626; margin:0; }
.offer-doc-date { font-size:12px; color:#7a90b0; margin:0; }
.offer-doc-candidate { font-size:16px; font-weight:700; margin:0 0 2px; }
.offer-doc-email { font-size:12px; color:#7a90b0; margin:0 0 20px; }
.offer-doc-divider { height:1px; background:#e4eaf2; margin:20px 0; }
.offer-doc-salutation { font-size:14px; font-weight:600; margin:0 0 16px; }
.offer-doc-body { font-size:13px; line-height:1.8; color:#1a2b45; }
.offer-doc-body :deep(p) { margin:0 0 10px; }
.offer-doc-body :deep(ul), .offer-doc-body :deep(ol) { margin:0 0 10px; padding-left:20px; }
.offer-doc-signatories { display:grid; grid-template-columns:1fr 1fr; gap:40px; margin-top:32px; }
.offer-doc-signatory { display:flex; flex-direction:column; gap:4px; }
.offer-sig-line { height:1px; background:#4a5e78; width:80%; margin-bottom:6px; }
.offer-sig-name { font-size:13px; font-weight:700; color:#1a2b45; margin:0; }
.offer-sig-title { font-size:11px; color:#7a90b0; margin:0; }

/* ── Interviews sub-nav ──────────────────────────────────────────── */
.int-subnav { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; margin-bottom:4px; }
.int-subnav-tabs { display:flex; gap:4px; background:#f0f4f8; padding:4px; border-radius:10px; }
.int-subnav-tab { padding:6px 14px; border-radius:7px; border:none; background:transparent; font-size:12px; font-weight:600; color:#7a90b0; cursor:pointer; transition:all .15s; }
.int-subnav-tab--active { background:#fff; color:#004AAD; box-shadow:0 1px 4px rgba(0,0,0,.08); }

/* ── Interview board cards ───────────────────────────────────────── */
.int-board { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:14px; margin-top:0; }
.int-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:16px; display:flex; flex-direction:column; gap:10px; transition:box-shadow .15s; }
.int-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.08); }
.int-card--cancelled { opacity:.6; }
.int-card-head { display:flex; align-items:center; gap:10px; }
.int-av-circle { width:38px; height:38px; background:#dbeafe; color:#1d4ed8; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; flex-shrink:0; }
.int-card-name { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.int-card-role { font-size:11px; color:#7a90b0; margin:0; }
.int-card-badges { display:flex; gap:6px; flex-wrap:wrap; }
.int-type-badge { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; text-transform:capitalize; }
.type-badge--virtual  { background:#e0f2fe; color:#0369a1; }
.type-badge--physical { background:#dcfce7; color:#15803d; }
.type-badge--phone    { background:#ede9fe; color:#6d28d9; }
.type-badge--other    { background:#f1f5f9; color:#64748b; }
.int-card-dt { display:flex; align-items:center; gap:7px; font-size:12px; color:#4a5e78; }
.int-card-icon { color:#7a90b0; width:13px; }
.int-link { color:#004AAD; text-decoration:none; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:200px; display:inline-block; }
.int-card-ivs { display:flex; flex-wrap:wrap; gap:4px; }
.int-card-actions { display:flex; gap:4px; border-top:1px solid #f0f4f8; padding-top:8px; margin-top:auto; }
.empty-board-msg { grid-column:1/-1; text-align:center; color:#aab8cc; padding:48px 0; font-size:13px; }

/* ── Assessment criteria card (detail modal) ─────────────────────── */
.assess-criterion-card { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:14px; display:flex; flex-direction:column; gap:8px; }
.assess-criterion-head { display:flex; align-items:center; justify-content:space-between; gap:8px; }
.assess-score-row { display:flex; align-items:center; gap:10px; }

/* ── Recommendation cards ────────────────────────────────────────── */
.recommendation-cards { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.rec-card { display:flex; flex-direction:column; align-items:center; gap:6px; padding:14px 10px; border-radius:10px; border:2px solid transparent; cursor:pointer; font-size:12px; font-weight:600; text-align:center; transition:all .2s; }
.rec-card--reject { background:#fff1f2; color:#be123c; border-color:#fda4af; }
.rec-card--reject.rec-card--selected { background:#fecdd3; border-color:#e11d48; }
.rec-card--ok { background:#f0fdf4; color:#15803d; border-color:#86efac; }
.rec-card--ok.rec-card--selected { background:#bbf7d0; border-color:#16a34a; }
.rec-card--strong { background:#fefce8; color:#a16207; border-color:#fde047; }
.rec-card--strong.rec-card--selected { background:#fef08a; border-color:#ca8a04; }
.rec-card:hover { filter:brightness(.97); }
.rec-icon { font-size:22px; }

/* ── Assessment template criteria add row ────────────────────────── */
.criteria-add-grid { display:grid; grid-template-columns:1fr 1fr 100px auto; gap:8px; align-items:center; margin-bottom:8px; }
@media (max-width:640px) { .criteria-add-grid { grid-template-columns:1fr 1fr; } }

/* ── Interviewer multi-select ──────────────────────────────────────── */
.ms-wrap       { position:relative; }
.ms-control    { display:flex; align-items:flex-start; justify-content:space-between;
                 gap:6px; padding:6px 10px; min-height:42px; border:1px solid #dde5f0;
                 border-radius:8px; background:#fff; cursor:pointer; transition:border-color .15s; }
.ms-control:hover,
.ms-control--open { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.ms-chips      { display:flex; flex-wrap:wrap; gap:4px; flex:1; align-items:center; }
.ms-chip       { display:inline-flex; align-items:center; gap:3px; background:#dbeafe;
                 color:#1d4ed8; border-radius:6px; padding:3px 8px; font-size:12px; font-weight:600; }
.ms-chip-x     { background:none; border:none; cursor:pointer; padding:0; line-height:1;
                 color:#1d4ed8; font-size:15px; opacity:.65; display:flex; align-items:center; }
.ms-chip-x:hover { opacity:1; }
.ms-input      { border:none; outline:none; font-size:13px; color:#1a2b45;
                 min-width:100px; flex:1; padding:1px 0; background:transparent; }
.ms-caret      { font-size:12px; color:#7a90b0; align-self:center; flex-shrink:0;
                 transition:transform .2s; user-select:none; }
.ms-caret--up  { transform:rotate(180deg); }
.ms-dropdown   { position:absolute; top:calc(100% + 4px); left:0; right:0; z-index:300;
                 background:#fff; border:1px solid #dde5f0; border-radius:10px;
                 box-shadow:0 10px 30px rgba(0,0,0,.12); max-height:210px; overflow-y:auto; }
.ms-option     { display:flex; align-items:center; gap:8px; padding:8px 12px;
                 cursor:pointer; font-size:13px; color:#1a2b45; transition:background .1s; }
.ms-option:hover  { background:#f4f7fb; }
.ms-option--on    { background:#eff6ff; }
.ms-option--on:hover { background:#dbeafe; }
.ms-tick       { width:16px; font-size:12px; color:#004AAD; font-weight:700; flex-shrink:0; }
.ms-av         { width:28px; height:28px; background:#c7d7f8; color:#1d4ed8;
                 border-radius:50%; display:inline-flex; align-items:center;
                 justify-content:center; font-size:10px; font-weight:700; flex-shrink:0; }
.ms-empty      { padding:14px; text-align:center; color:#7a90b0; font-size:13px; }

/* ── Job Posting Form ── */
.posting-form-body { display:flex; flex-direction:column; gap:0; }
.posting-section { padding:20px 0; border-bottom:1px solid #eef2f7; }
.posting-section:last-child { border-bottom:none; }
.posting-section-title { font-size:13px; font-weight:700; color:#004AAD; text-transform:uppercase; letter-spacing:.04em; margin:0 0 14px; }
.posting-checkboxes { display:flex; gap:24px; flex-wrap:wrap; margin-top:10px; }
.toggle-label { display:flex; align-items:center; font-size:13px; color:#3d4d6a; font-weight:500; cursor:pointer; }
.posting-channels { display:flex; gap:10px; flex-wrap:wrap; }
.channel-chip { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border-radius:20px; border:1.5px solid #d4dce8; font-size:12px; font-weight:600; color:#5a738a; cursor:pointer; transition:all .15s; user-select:none; background:#f7f9fc; }
.channel-chip--active { background:#e8f0fe; border-color:#004AAD; color:#004AAD; }
.channel-chip:hover { border-color:#b0c4de; }
</style>
